<template>
  <div class="design-root">
    <div class="design-context-nav">
      <b-navbar type="dark" variant="primary" toggleable="md" id="context-nav" style="width: 100%;">
        <b-navbar-toggle target="nav_dropdown_collapse"></b-navbar-toggle>
        <b-collapse is-nav id="nav_dropdown_collapse">
          <div class="context-nav-actions">
            <b-button @click="showCreatePage">new page</b-button>
            <b-button @click="showEditPage">edit page</b-button>
            <b-button data-embed-page @click="showPageEmbed">embed</b-button>
            <b-button class="context-nav-danger" @click="showDeletePage">delete page</b-button>
            <b-button @click.prevent="showEditMenu">edit menu</b-button>
            <b-button @click="showAppCssEditor" class="app-css-btn">
              <font-awesome-icon icon="palette" />
              app css
            </b-button>
            <b-button @click="previewVisible = !previewVisible" :class="{ 'preview-toggle-active': previewVisible }" class="preview-toggle-btn context-nav-end">
              <font-awesome-icon icon="eye" v-if="previewVisible" />
              <font-awesome-icon icon="eye-slash" v-else />
              preview
            </b-button>
          </div>
        </b-collapse>
      </b-navbar>
    </div>

    <div class="design-main" :class="{ 'design-main-stacked': compactDesign && previewVisible }">
      <!-- Left pane: sidebar + page editor -->
      <div class="design-editor-pane" :style="editorPaneStyle">
        <ViewCssEditor ref="viewCssEditorRef" />
        <b-container fluid style="height: 100%">
          <b-row style="height: 100%">
            <b-col id="page-list-col" cols="3">
              <div class="sidebar">
                <div class="sidebar-section-label">Pages</div>
                <draggable
                  :list="sidebarTree"
                  item-key="uniqueTitle"
                  :group="{ name: 'page-tree', pull: true, put: true }"
                  handle=".drag-handle"
                  :filter="pageDragFilter"
                  :preventOnFilter="false"
                  :force-fallback="true"
                  :fallback-on-body="true"
                  :fallback-tolerance="4"
                  fallback-class="page-drag-fallback"
                  data-page-parent=""
                  ghost-class="drag-ghost"
                  :empty-insert-threshold="28"
                  :move="canMovePage"
                  class="page-tree-root"
                  @start="onPageDragStart"
                  @unchoose="clearPageDrag"
                  @end="onPageDragEnd"
                >
                  <template #header>
                    <div v-show="showRootDrop" class="page-root-drop">top level</div>
                  </template>
                  <template #item="{ element: sidebarPage }">
                    <pageTree
                      :page="sidebarPage"
                      :depth="0"
                      :active-unique-title="page && page.uniqueTitle"
                      :on-load-page="loadPage"
                      :on-drag-start="onPageDragStart"
                      :on-drag-clear="clearPageDrag"
                      :on-drag-end="onPageDragEnd"
                      :can-move-page="(evt) => canMovePage(evt)"
                      :drag-filter="pageDragFilter"
                      :pages="pages"
                      @reorder="movePageByKey"
                    />
                  </template>
                  <template #footer>
                    <div v-show="showRootDrop" class="page-root-drop">top level</div>
                  </template>
                </draggable>
              </div>
            </b-col>
            <b-col cols="9" class="design-canvas-col">
              <div class="content-header" v-if="page && page.title">
                <span class="content-title">{{ page.title }}</span>
                <span v-if="showsSeparateId(page.title, page.uniqueTitle)" class="content-unique-title">{{ page.uniqueTitle }}</span>
                <span v-if="pageIsModal(page)" class="content-modal-caption">opens in a modal</span>
                <button type="button" @click="showPageCssEditor" class="css-edit-btn page-css-btn" v-hover-tip="viewIconTip('pageCss')">
                  <font-awesome-icon icon="palette" />
                </button>
                <button type="button" class="content-header-lock" :class="{ active: pageRequiresLogin }" @click="toggleRequiresLogin" v-hover-tip="pageLoginTip(page)">
                  <font-awesome-icon :icon="pageRequiresLogin ? 'lock' : 'lock-open'" />
                </button>
                <button
                  v-if="pageRequiresLogin"
                  type="button"
                  class="content-header-lock"
                  :class="{ active: hasUserPermissionRules(page) }"
                  @click="openUserPermissionRules(page, 'this page')"
                  v-hover-tip="userPermissionTip(hasUserPermissionRules(page), 'this page')"
                  :aria-label="userPermissionTip(hasUserPermissionRules(page), 'this page')"
                >
                  <font-awesome-icon icon="user-shield" />
                </button>
                <button
                  type="button"
                  class="content-header-lock"
                  :class="{ active: hasPageOpenRules(page) }"
                  @click="openPageOpenRules"
                  v-hover-tip="pageOpenTip(hasPageOpenRules(page))"
                  :aria-label="pageOpenTip(hasPageOpenRules(page))"
                >
                  <font-awesome-icon icon="message" />
                </button>
                <span v-if="hasPageOpenRules(page)" class="content-modal-caption">presentation</span>
              </div>
              <div class="layout-canvas-wrap">
                <div v-if="!(page && page.uniqueTitle)" class="content-empty">
                  <p>Create a page to start designing.</p>
                  <b-button size="sm" @click="showCreatePage">new page</b-button>
                </div>
                <div v-else-if="page.components" class="layout-canvas">
                  <section
                    v-for="(component, componentIndex) in page.components"
                    :key="componentIndex"
                    class="layout-row"
                  >
                      <header class="layout-chrome">
                      <span class="layout-label">row {{ componentIndex + 1 }}</span>
                      <button type="button" @click="removeComponent(page, component)" class="delete" aria-label="Remove row">x</button>
                    </header>
                    <div class="layout-columns" :class="{ 'layout-columns-sized': rowHasColumnWidth(component) }">
                      <section
                        v-for="(subComponent, subComponentIndex) in component.components"
                        :key="subComponentIndex"
                        class="layout-column"
                        :style="columnCanvasStyle(subComponent)"
                      >
                        <header class="layout-chrome">
                          <span class="layout-label">
                            column {{ subComponentIndex + 1 }}
                            <span v-if="columnFormatLabel(subComponent)" class="layout-width-label">{{ columnFormatLabel(subComponent) }}</span>
                          </span>
                          <span class="layout-chrome-actions">
                            <button
                              type="button"
                              class="css-edit-btn layout-format-btn"
                              :class="{ active: !!columnFormatLabel(subComponent) }"
                              @click="openColumnFormat(subComponent)"
                              v-hover-tip="viewIconTip('columnFormat')"
                              :aria-label="viewIconTip('columnFormat')"
                            >
                              <font-awesome-icon icon="text-width" />
                            </button>
                            <button type="button" @click="removeComponent(component, subComponent)" class="delete" aria-label="Remove column">x</button>
                          </span>
                        </header>
                        <draggable
                          v-model="subComponent.components"
                          group="page-views"
                          :item-key="(item) => item.uniqueTitle || item.type + '-' + (item.title || '')"
                          handle=".view-drag-handle"
                          ghost-class="view-drag-ghost"
                          class="layout-views"
                          @end="onViewDragEnd"
                        >
                          <template #item="{ element: subSubComponent }">
                            <article class="view-card">
                              <header class="view-card-bar">
                                <span class="view-card-type">
                                  <DragHandle
                                    handle-class="view-drag-handle"
                                    :reorder-id="subSubComponent.uniqueTitle"
                                    v-hover-tip="viewIconTip('drag')"
                                    @move="moveViewByKey(subSubComponent, $event)"
                                  />
                                  {{ subSubComponent.type }}
                                </span>
                                <span class="view-card-actions">
                                  <button type="button" @click.stop="showViewTransfer(subSubComponent, 'duplicate')" class="css-edit-btn" v-hover-tip="viewIconTip('duplicate')" :aria-label="viewIconTip('duplicate')">
                                    <font-awesome-icon icon="copy" />
                                  </button>
                                  <button type="button" @click.stop="showViewTransfer(subSubComponent, 'move')" class="css-edit-btn" v-hover-tip="viewIconTip('move')" :aria-label="viewIconTip('move')">
                                    <font-awesome-icon icon="arrow-right" />
                                  </button>
                                  <button type="button" @click.stop="showCssEditor(subSubComponent)" class="css-edit-btn" v-hover-tip="viewIconTip('css')">
                                    <font-awesome-icon icon="palette" />
                                  </button>
                                  <button type="button" data-embed-view @click.stop="showViewEmbed(subSubComponent)" class="css-edit-btn" v-hover-tip="viewIconTip('embed')" :aria-label="viewIconTip('embed')">
                                    <font-awesome-icon icon="code" />
                                  </button>
                                  <button type="button" @click.stop="removeComponent(subComponent, subSubComponent)" class="delete" aria-label="Remove view" v-hover-tip="viewIconTip('remove')">
                                    x
                                  </button>
                                </span>
                              </header>
                              <button type="button" class="view-card-open" @click="showEditComponent(subSubComponent)">
                                <span class="view-card-title" :class="{ 'is-placeholder': !subSubComponent.title }">{{ subSubComponent.title || 'no title' }}</span>
                                <span class="view-card-meta">
                                  <span v-if="subSubComponent.uniqueTitle" class="view-card-slug" :class="{ 'visually-hidden': !showsSeparateId(subSubComponent.title, subSubComponent.uniqueTitle) }">{{ subSubComponent.uniqueTitle }}</span>
                                  <span v-if="subSubComponent.type === 'menu'" class="view-card-slug">{{ menuItemSummary(subSubComponent) }}</span>
                                </span>
                              </button>
                              <div v-if="defaultValueRuleBadge(subSubComponent)" class="view-card-body">
                                <span
                                  class="view-default-rule-badge"
                                  v-hover-tip="defaultValueRuleBadge(subSubComponent).title"
                                >
                                  <font-awesome-icon icon="bolt" />
                                  {{ defaultValueRuleBadge(subSubComponent).label }}
                                </span>
                              </div>
                              <div v-if="showViewLoginControls(subSubComponent) || showUserPermissionButton(subSubComponent)" class="view-login-controls">
                                <button
                                  v-if="showViewLoginControls(subSubComponent)"
                                  type="button"
                                  class="view-login-btn"
                                  :class="{ active: subSubComponent.requiresLogin }"
                                  @click.stop="toggleViewLoginFlag(subSubComponent, 'requiresLogin')"
                                  v-hover-tip="viewLoginTip(subSubComponent)"
                                >
                                  <font-awesome-icon :icon="subSubComponent.requiresLogin ? 'lock' : 'lock-open'" />
                                </button>
                                <button
                                  v-if="showViewLoginControls(subSubComponent)"
                                  type="button"
                                  class="view-login-btn view-hide-logged-in-btn"
                                  :class="{ active: subSubComponent.hideWhenLoggedIn }"
                                  @click.stop="toggleViewLoginFlag(subSubComponent, 'hideWhenLoggedIn')"
                                  v-hover-tip="viewHideWhenLoggedInTip(subSubComponent)"
                                >
                                  <font-awesome-icon :icon="subSubComponent.hideWhenLoggedIn ? 'eye-slash' : 'eye'" />
                                </button>
                                <button
                                  v-if="showUserPermissionButton(subSubComponent)"
                                  type="button"
                                  class="view-login-btn"
                                  :class="{ active: hasUserPermissionRules(subSubComponent) }"
                                  @click.stop="openUserPermissionRules(subSubComponent, 'this view')"
                                  v-hover-tip="userPermissionTip(hasUserPermissionRules(subSubComponent), 'this view')"
                                  :aria-label="userPermissionTip(hasUserPermissionRules(subSubComponent), 'this view')"
                                >
                                  <font-awesome-icon icon="user-shield" />
                                </button>
                                <span v-if="subSubComponent.requiresLogin" class="view-login-caption">only when logged in</span>
                                <span v-else-if="subSubComponent.hideWhenLoggedIn" class="view-login-caption view-login-caption-logged-in">hidden when logged in</span>
                                <span v-if="hasUserPermissionRules(subSubComponent)" class="view-login-caption">user rules</span>
                              </div>
                              <footer v-if="subSubComponent.spreadSheet" class="view-card-foot">
                                <span v-hover-tip="viewSpreadsheetTip(subSubComponent)">
                                  <font-awesome-icon icon="table" />
                                  {{ getSpreadSheetTitle(subSubComponent.spreadSheet) }}
                                  <span v-if="subSubComponent.filterByPageRecord" class="view-card-foot-via">
                                    (page record<span v-if="getRelationshipColumnTitle(subSubComponent.spreadSheet)"> via {{ getRelationshipColumnTitle(subSubComponent.spreadSheet) }}</span>)
                                  </span>
                                </span>
                                <span
                                  v-if="isLoginSpreadSheet(subSubComponent.spreadSheet)"
                                  class="login-sheet-badge"
                                  v-hover-tip="loginNoteForSpreadSheet(subSubComponent.spreadSheet)"
                                >
                                  <font-awesome-icon icon="key" />
                                  login
                                </span>
                              </footer>
                            </article>
                          </template>
                        </draggable>
                        <button type="button" class="layout-add" @click="showAddComponent(subComponent)">
                          + add component
                        </button>
                      </section>
                    </div>
                    <button type="button" class="layout-add layout-add-column" @click="addColumn(component)">
                      + add column
                    </button>
                  </section>
                  <button type="button" class="layout-add layout-add-row" @click="addRow(page)">
                    + add row
                  </button>
                </div>
              </div>
            </b-col>
          </b-row>
        </b-container>
      </div>

      <!-- Draggable divider -->
      <div
        v-show="previewVisible"
        class="design-divider"
        @mousedown="onDividerMouseDown"
        :class="{ 'design-divider-dragging': isDraggingDivider }"
      >
        <div class="design-divider-handle"></div>
      </div>

      <!-- Right pane: preview iframe -->
      <div v-show="previewVisible" class="design-preview-pane" :class="{ 'is-waiting': !previewReady }">
        <div class="preview-header">
          <span class="preview-header-label">preview</span>
          <span class="preview-header-status" :class="{ connected: previewReady, stalled: previewStalled && !previewReady }">
            {{ previewStatusLabel }}
          </span>
        </div>
        <div v-if="previewUrl && !previewReady" class="preview-waiting">
          <p>{{ previewStalled ? 'The live app did not connect.' : 'Loading the live app…' }}</p>
          <p v-if="previewStalled" class="preview-waiting-note">Use view live to open the app, or turn preview off and on.</p>
        </div>
        <iframe
          v-if="previewUrl"
          ref="previewIframe"
          :src="previewUrl"
          class="preview-iframe"
          @load="onPreviewIframeLoad"
        ></iframe>
        <div v-else class="preview-placeholder">
          select a page to preview
        </div>
      </div>
    </div>

    <b-modal
      v-model="createPageModalVisible"
      title="create new page"
      @ok="onCreatePageModalOk"
      :ok-disabled="createPageAiSubmitting"
      :ok-title="createPageActiveTab === 1 ? (createPageAiSubmitting ? 'generating...' : 'create with AI') : 'OK'"
    >
      <template #title>
        create new page
        <modal-help topic="create-page" />
      </template>
      <b-tabs v-model="createPageActiveTab">
        <b-tab title="manual" active>
          <b-form>
            <b-form-group label="page title" :invalid-feedback="newPageTitleFeedback" :state="newPageTitleState">
              <b-form-input v-model="newPageTitle" :state="newPageTitleState" placeholder="my new page name" required />
            </b-form-group>
            <b-form-group label="unique title" :invalid-feedback="newPageUniqueTitleFeedback" :state="newPageUniqueTitleState">
              <b-form-input :value="newPageUniqueTitle" @input="onPageUniqueTitleInput" :state="newPageUniqueTitleState" placeholder="my_new_page_name" required />
            </b-form-group>
          </b-form>
        </b-tab>
        <b-tab title="with AI">
          <b-form>
            <b-form-group label="describe the page you want">
              <b-form-textarea
                v-model="aiPagePrompt"
                placeholder="e.g. a contacts page with a table of people, a form to add one, and a short introduction"
                rows="4"
                max-rows="10"
                :disabled="createPageAiSubmitting"
              />
              <small class="text-muted">The AI will create the page and its views. It uses spreadsheets already in this app, and can add a spreadsheet when the page needs new data.</small>
              <AiTokenUsage />
            </b-form-group>
            <b-alert v-if="aiPageError" :model-value="true" variant="danger" dismissible @closed="aiPageError = null">{{ aiPageError }}</b-alert>
            <div v-if="createPageAiSubmitting" class="text-muted">
              Generating page with AI... this can take up to a minute.
            </div>
          </b-form>
        </b-tab>
      </b-tabs>
    </b-modal>

    <b-modal v-model="embedModalVisible" title="embed" ok-only ok-title="close" size="lg">
      <template #title>
        embed
        <modal-help topic="embed-snippet" />
      </template>
      <EmbedChooser
        v-if="embedModalVisible"
        :key="embedChooserKey"
        :application="$route.params.application"
        :pages="pages"
        :start-mode="embedStartMode"
        :start-page="embedStartPage"
        :start-view="embedStartView"
      />
    </b-modal>

    <b-modal v-model="editPageModalVisible" title="edit page" @ok="editPage" modal-class="view-editor-modal">
      <template #title>
        edit page
        <modal-help topic="edit-page" />
      </template>
      <div class="edit-modal-content">
        <div class="tab-section">
          <div class="section-heading">Naming</div>
          <div class="field-row">
            <label class="field-label">
              page title
              <b-form-input v-model="editPageTitle" placeholder="my page name" required />
            </label>
          </div>
          <div class="field-row">
            <label class="field-label">
              unique title
              <b-form-input v-model="editPageUniqueTitle" placeholder="my_page_name" required />
            </label>
          </div>
        </div>
        <div class="tab-section">
          <div class="section-heading">Options</div>
          <label v-if="!page.parentUniqueTitle" class="checkbox-label">
            <input type="checkbox" v-model="editPageHome" />
            set as home page
          </label>
          <label class="checkbox-label">
            <input type="checkbox" v-model="editPageShowBreadcrumbs" />
            show breadcrumb navigation
          </label>
          <label class="checkbox-label">
            <input type="checkbox" v-model="editPageOpenAsModal" />
            open in a modal when linked from another page
          </label>
          <small class="text-muted">A link from another page opens this page in a dialog. Visiting its address directly still shows the full page.</small>
        </div>
        <div class="tab-section">
          <div class="section-heading">Copy</div>
          <p v-if="pageCopyPreview" class="section-desc">
            Creates {{ pageCopyPreview.title }}
            (<span class="copy-page-slug">{{ pageCopyPreview.uniqueTitle }}</span>)<span v-if="copyIncludesSubPages"> and its sub-pages</span>.
          </p>
          <label v-if="pageSubPageCount" class="checkbox-label copy-subpages-label">
            <input type="checkbox" v-model="copyPageWithSubPages" />
            copy {{ pageSubPageCount }} sub-page{{ pageSubPageCount === 1 ? '' : 's' }}
          </label>
          <button type="button" class="copy-page-btn" @click="copyPage" :disabled="copyingPage || !page.uniqueTitle">
            {{ copyingPage ? 'copying...' : 'copy page' }}
          </button>
          <p v-if="copyPageError" class="copy-page-error">{{ copyPageError }}</p>
        </div>
      </div>
    </b-modal>

    <b-modal
      v-model="viewTransferModalVisible"
      :title="viewTransferMode === 'move' ? 'move view' : 'duplicate view'"
      @ok="confirmViewTransfer"
      :ok-disabled="!viewTransferCanConfirm"
      :ok-title="viewTransferSubmitting ? 'saving...' : (viewTransferMode === 'move' ? 'move' : 'duplicate')"
    >
      <template #title>
        {{ viewTransferMode === 'move' ? 'move view' : 'duplicate view' }}
        <modal-help topic="transfer-view" />
      </template>
      <b-form>
        <b-form-group label="page">
          <b-form-select
            v-model="viewTransferDestination"
            :options="viewTransferPageOptions"
            :disabled="viewTransferSubmitting || !viewTransferPageOptions.length"
          />
          <small v-if="viewTransferView && viewTransferView.spreadSheet" class="text-muted">Pages that use this view's spreadsheet, or a spreadsheet linked to it.</small>
          <small v-else class="text-muted">Any page. This view does not read a spreadsheet.</small>
        </b-form-group>
        <b-form-group label="unique title" :state="viewTransferTitleState" :invalid-feedback="viewTransferTitleError">
          <b-form-input v-model="viewTransferUniqueTitle" :state="viewTransferTitleState" :disabled="viewTransferSubmitting" />
        </b-form-group>
        <p v-if="viewTransferPlan.uniqueTitle" class="copy-page-preview">
          {{ viewTransferMode === 'move' ? 'Moves' : 'Creates' }}
          {{ viewTransferPlan.title || 'this view' }}
          (<span class="copy-page-slug">{{ viewTransferPlan.uniqueTitle }}</span>).
        </p>
        <p v-if="viewTransferPlan.filterByPageRecord" class="copy-page-preview">
          The destination page is opened from a parent record, so the view will only show records related to that record<span v-if="viewTransferPlan.relationshipTitle"> via {{ viewTransferPlan.relationshipTitle }}</span>.
        </p>
        <p v-else-if="viewTransferPlan.clearsPageRecord" class="copy-page-preview">
          The destination page is not opened from a parent record of this spreadsheet, so the page-record limit is removed.
        </p>
        <p v-if="viewTransferError" class="copy-page-error">{{ viewTransferError }}</p>
        <p v-else-if="viewTransferPlan.pageError" class="copy-page-error">{{ viewTransferPlan.pageError }}</p>
      </b-form>
    </b-modal>

    <b-modal v-model="deletePageModalVisible" title="delete page" @ok="deletePage" ok-variant="danger" ok-title="delete">
      <template #title>
        delete page
        <modal-help topic="delete-page" />
      </template>
      <b-container fluid>
        delete {{ page.uniqueTitle }}?
      </b-container>
    </b-modal>

    <b-modal v-model="createComponentModalVisible" title="create new component" @ok="createComponent" size="lg" scrollable body-class="create-component-modal-body">
      <template #title>
        create new component
        <modal-help topic="create-component" />
      </template>
      <b-container fluid>
        <b-form>
          <b-row>
            <b-form-group label="component title" :invalid-feedback="newComponentTitleFeedback" :state="newComponentTitleState">
              <b-form-input v-model="newComponentTitle" @input="onComponentTitleInput" :state="newComponentTitleState" placeholder="my new component name" required />
            </b-form-group>
          </b-row>
          <b-row>
            <b-form-group label="unique title" :invalid-feedback="newComponentUniqueTitleFeedback" :state="newComponentUniqueTitleState">
              <b-form-input :value="newComponentUniqueTitle" @input="onComponentUniqueTitleInput" :state="newComponentUniqueTitleState" placeholder="my_new_component_name" required />
            </b-form-group>
          </b-row>
          <b-row>
            <b-form-group label="component type" :invalid-feedback="newComponentTypeFeedback" :state="newComponentTypeState">
              <p class="component-type-hint">Pick what visitors should see. Each option says what it is for.</p>
              <ComponentTypePicker v-model="newComponentType" :invalid="newComponentTypeState === false" />
            </b-form-group>
          </b-row>
          <b-row v-if="componentTypeRequiresSpreadsheet">
            <b-form-group label="component spreadsheet" :invalid-feedback="newComponentSpreadSheetFeedback" :state="newComponentSpreadSheetState">
              <b-form-select v-model="newComponentSpreadSheet" :options="enrichedSpreadSheetOptions" :state="newComponentSpreadSheetState" class="mb-3" size="sm" placeholder="select component spreadsheet" required />
            </b-form-group>
          </b-row>
          <b-row v-if="newComponentSpreadSheet && componentTypeSupportsAddAllFields">
            <b-form-group>
              <b-form-checkbox v-model="addAllFieldsFromSpreadsheet">
                add all columns from spreadsheet {{ selectedSpreadsheetTitle }}
              </b-form-checkbox>
            </b-form-group>
          </b-row>
        </b-form>
      </b-container>
    </b-modal>

    <b-modal
      v-model="columnFormatModalVisible"
      title="column formatting"
      @ok="saveColumnFormat"
      ok-title="save"
    >
      <template #title>
        column formatting
        <modal-help topic="column-format" />
      </template>
      <p class="component-type-hint">Percent is a share of the row. Pixels are a fixed width. Leave the width blank and this column fills the space that is left.</p>
      <b-form-group label="width" :state="columnFormatState" :invalid-feedback="columnFormatError">
        <b-input-group>
          <b-form-input
            v-model="columnFormatWidth"
            type="number"
            min="1"
            :max="columnFormatUnit === 'percent' ? 100 : 4000"
            placeholder="auto"
            @update:model-value="columnFormatError = ''"
          />
          <b-form-select v-model="columnFormatUnit" :options="columnFormatUnitOptions" />
        </b-input-group>
      </b-form-group>
      <b-form-group label="when a view is wider than this column">
        <b-form-select v-model="columnFormatOverflow" :options="columnFormatOverflowOptions" />
      </b-form-group>
      <p class="component-type-hint">Scroll keeps the normal layout and moves sideways. Mobile layout uses the arrangement the view uses on a small screen, such as a stacked table, one card across, or a compact calendar.</p>
    </b-modal>

    <formEdit ref="formEditRef"></formEdit>
    <tableEdit ref="tableEditRef"></tableEdit>
    <pivotEdit ref="pivotEditRef"></pivotEdit>
    <customEdit ref="customEditRef"></customEdit>
    <cardsEdit ref="cardsEditRef"></cardsEdit>
    <chartEdit ref="chartEditRef"></chartEdit>
    <richtextEdit ref="richtextEditRef"></richtextEdit>
    <staticImageEdit ref="staticImageEditRef"></staticImageEdit>
    <menuEdit ref="menuEditRef"></menuEdit>
    <UserPermissionRuleEditor
      :visible="permissionEditorVisible"
      :rules="permissionEditorRules"
      :spreadsheets="spreadsheets"
      :pages="pages"
      :subject="permissionSubject"
      :application-unique-title="$route.params.application"
      @update:visible="permissionEditorVisible = $event"
      @save="saveUserPermissionRules"
    />
    <PageOpenRuleEditor
      :visible="pageOpenEditorVisible"
      :page="page"
      :pages="pages"
      :spreadsheets="spreadsheets"
      @update:visible="pageOpenEditorVisible = $event"
      @save="savePageOpenRules"
    />
  </div>
</template>
<script>

  import _ from 'lodash'
  import draggable from 'vuedraggable'
  import pageTree from '../pagetree.vue'
  import DragHandle from '../DragHandle.vue'
  import formEdit from '../formedit.vue'
  import tableEdit from '../tableedit.vue'
  import pivotEdit from '../pivotedit.vue'
  import customEdit from '../customedit.vue'
  import cardsEdit from '../cardsedit.vue'
  import chartEdit from '../chartedit.vue'
  import richtextEdit from '../richtextedit.vue'
  import staticImageEdit from '../staticimageedit.vue'
  import menuEdit from '../menuedit.vue'
  import ViewCssEditor from '../ViewCssEditor.vue'
  import AiTokenUsage from '../AiTokenUsage.vue'
  import { buildComponentsFromSpreadsheetColumns, viewSupportsAddAllColumns } from '../../lib/spreadsheet-view-fields.js'
  import { ancestorRequiresLogin, nextViewLoginFlags, pageIsInLoginProtectedStructure, pageRequiresLogin as pageRequiresLoginFromAncestors, shouldOfferViewLoginControls } from '../../../../shared/view-visibility.js'
  import { hasUserPermissionRules } from '../../../../shared/user-permission-rules.js'
  import { hasPageOpenRules } from '../../../../shared/page-open-rules.js'
  import { loginSpreadSheetIndex, loginSpreadSheetNote } from '../../../../shared/logged-in-user-default.js'
  import { sanitizeSorts } from '../../../../shared/view-sort.js'
  import { normalizeFilterButtons } from '../../../../shared/filter-buttons.js'
  import { cardsAcross, normalizeCardsPerRow } from '../../../../shared/card-layout.js'
  import { columnDesignerStyle, columnFormatLabel as formatColumnWidth, columnFormatProblem, columnFormatRecord } from '../../../../shared/column-format.js'
  import { liveAppHref } from '../../../../shared/app-host.js'
  import EmbedChooser from '../EmbedChooser.vue'
  import { pageOpensAsModal } from '../../../../shared/page-modal.js'
  import { descendantPages, planPageCopy } from '../../../../shared/page-copy.js'
  import { planViewTransfer, suggestedViewUniqueTitle, viewTransferDestinations as pagesForViewTransfer } from '../../../../shared/view-copy.js'
  import { applyPageParents, movePageInTree, normalizePageArrangement, pagePlacementFromTree, pageTreeFromPages } from '../../../../shared/page-tree.js'
  import { moveListItem } from '../../../../shared/list-move.js'
  import { defaultValueRuleBadge } from '../../lib/default-value-rule-badge.js'
  import { hoverTip } from '../../lib/hover-tip.js'
  import { viewIconTip, viewLoginTip, viewHideWhenLoggedInTip, viewSpreadsheetTip, pageLoginTip as pageLoginTipText, userPermissionTip, pageOpenTip } from '../../lib/view-icon-tips.js'
  import UserPermissionRuleEditor from '../lib/UserPermissionRuleEditor.vue'
  import PageOpenRuleEditor from '../lib/PageOpenRuleEditor.vue'
  import { findPageRecordSpreadSheet, relatedSpreadSheetUniqueTitles, pageRecordRelationship, syncSavedPage, applyPageRecordFilter, canPlacePageUnder, pageDragFilter as pageNestDragFilter } from '../../lib/page-record-link.js'
  import { rememberDesignerSelection } from '../../lib/designer-selection.js'
  import { onDesignerChange, raiseDesignerWarning } from '../../lib/designer-sync.js'
  import { showsSeparateId } from '../../lib/designer-labels.js'
  import { componentTypeNeedsSpreadsheet } from '../../lib/component-type-catalog.js'
  import ComponentTypePicker from '../ComponentTypePicker.vue'

  export default {
    name: `design`,
    data () {

      return {
        pages: [],
        permissionEditorVisible: false,
        permissionTarget: null,
        permissionSubject: 'this page',
        pageOpenEditorVisible: false,
        sidebarTree: [],
        showRootDrop: false,
        arrangingPages: false,
        arrangementDirty: false,
        lastPageArrangement: ``,
        spreadsheets: [],
        spreadSheetOptions: [],
        page: {
          components: []
        },
        editorWidth: Math.min(Math.max(500, Math.floor(window.innerWidth * 0.55)), window.innerWidth - 306),
        compactDesign: window.innerWidth < 900,
        stackedEditorHeight: 0,
        isDraggingDivider: false,
        previewReady: false,
        previewStalled: false,
        previewWaitTimer: null,
        previewUrl: null,
        previewVisible: true,
        availableObjects: [
        ],
        newPageTitle: ``,
        newPageUniqueTitle: ``,
        newPageUniqueTitleManuallyEdited: false,
        newPageSubmitAttempted: false,
        createPageActiveTab: 0,
        aiPagePrompt: ``,
        aiPageError: null,
        createPageAiSubmitting: false,
        editPageTitle: ``,
        editPageHome: false,
        editPageUniqueTitle: ``,
        editPageShowBreadcrumbs: false,
        editPageOpenAsModal: false,
        copyPageWithSubPages: true,
        copyingPage: false,
        copyPageError: ``,
        viewTransferModalVisible: false,
        viewTransferMode: `duplicate`,
        viewTransferView: null,
        viewTransferDestination: ``,
        viewTransferUniqueTitle: ``,
        viewTransferError: ``,
        viewTransferSubmitting: false,
        columnFormatModalVisible: false,
        columnFormatTarget: null,
        columnFormatWidth: ``,
        columnFormatUnit: `percent`,
        columnFormatError: ``,
        columnFormatUnitOptions: [
          { value: `percent`, text: `percent` },
          { value: `pixel`, text: `pixels` }
        ],
        columnFormatOverflow: `scroll`,
        columnFormatOverflowOptions: [
          { value: `scroll`, text: `scroll sideways` },
          { value: `mobile`, text: `use the mobile layout` }
        ],
        editingPageUniqueTitle: ``,
        newComponentTitle: ``,
        newComponentUniqueTitle: ``,
        newComponentSpreadSheet: ``,
        newComponentType: ``,
        newComponentTitleManuallyEdited: false,
        newComponentUniqueTitleManuallyEdited: false,
        newComponentSubmitAttempted: false,
        addAllFieldsFromSpreadsheet: false,
        createPageModalVisible: false,
        editPageModalVisible: false,
        embedModalVisible: false,
        embedChooserKey: 0,
        embedStartMode: 'page',
        embedStartPage: '',
        embedStartView: '',
        deletePageModalVisible: false,
        createComponentModalVisible: false,
        appCustomCss: ''
      }
    },
    mounted () {

      window.design = this
      this.getPages()
      this._stopDesignerSync = onDesignerChange((change) => this.applyDesignerChange(change))
      this.previewUrl = this.buildPreviewUrl()
      this.armPreviewWait()
      window.addEventListener('message', this.onPreviewMessage)
      this._onDesignResize = () => this.syncCompactDesign()
      window.addEventListener('resize', this._onDesignResize)
    },
    beforeUnmount () {
      if (this._stopDesignerSync) this._stopDesignerSync()
      this.clearPageDrag()
      this.clearPreviewWait()
      window.removeEventListener('message', this.onPreviewMessage)
      if (this._onDesignResize) window.removeEventListener('resize', this._onDesignResize)
    },
    watch: {
      '$route.params.application' () {
        this.pages = []
        this.sidebarTree = []
        this.page = { components: [] }
        this.getPages()
        this.previewUrl = this.buildPreviewUrl()
        this.previewReady = false
        this.previewStalled = false
        this.armPreviewWait()
      },
      '$route.params.page' (uniqueTitle) {

        if (!uniqueTitle || !this.pages || !this.pages.length) {

          return
        }

        if (this.page && this.page.uniqueTitle === uniqueTitle) {

          return
        }

        const target = this.pages.find((item) => item.uniqueTitle === uniqueTitle)

        if (target) {

          this.loadPage(target)
        }
      },
      newPageTitle (val) {
        if (!this.newPageUniqueTitleManuallyEdited) {
          this.newPageUniqueTitle = this.sanitizeToUniqueTitle(val)
        }
      },
      newComponentTitle (val) {
        if (!this.newComponentUniqueTitleManuallyEdited) {
          this.newComponentUniqueTitle = this.sanitizeToUniqueTitle(val)
        }
      },
      newComponentSpreadSheet () {
        this.applyDefaultComponentTitle()
      },
      newComponentType () {
        this.applyDefaultComponentTitle()
      }
    },
    computed: {
      editorPaneStyle () {

        if (!this.previewVisible) return { flex: '1 1 auto', width: 'auto', minWidth: '0' }

        if (this.compactDesign) {

          if (this.stackedEditorHeight) {

            return { flex: 'none', height: `${this.stackedEditorHeight}px`, width: 'auto', minWidth: '0', minHeight: '0' }
          }

          return { flex: '1 1 0', width: 'auto', minWidth: '0', minHeight: '0' }
        }

        return { width: `${this.editorWidth}px` }
      },
      columnFormatState () {

        if (this.columnFormatError) return false

        return null
      },
      previewStatusLabel () {

        if (this.previewReady) return 'connected'

        if (this.previewStalled) return 'not connected'

        return 'loading…'
      },
      newPageTitleState () {
        if (this.newPageSubmitAttempted && !String(this.newPageTitle).trim().length) return false
        return null
      },
      newPageTitleFeedback () {
        if (this.newPageSubmitAttempted && !String(this.newPageTitle).trim().length) return 'a page title is required'
        return ''
      },
      newPageUniqueTitleState () {
        if (this.newPageSubmitAttempted && !String(this.newPageUniqueTitle).trim().length) return false
        return null
      },
      newPageUniqueTitleFeedback () {
        if (this.newPageSubmitAttempted && !String(this.newPageUniqueTitle).trim().length) return 'a unique title is required'
        return ''
      },
      newComponentTitleState () {
        if (this.newComponentSubmitAttempted && !String(this.newComponentTitle).trim().length) return false
        return null
      },
      newComponentTitleFeedback () {
        if (this.newComponentSubmitAttempted && !String(this.newComponentTitle).trim().length) return 'a component title is required'
        return ''
      },
      newComponentUniqueTitleState () {
        if (this.newComponentSubmitAttempted && !String(this.newComponentUniqueTitle).trim().length) return false
        return null
      },
      newComponentUniqueTitleFeedback () {
        if (this.newComponentSubmitAttempted && !String(this.newComponentUniqueTitle).trim().length) return 'a unique title is required'
        return ''
      },
      pageSubPageCount () {
        if (!this.page || !this.page.uniqueTitle) return 0
        return descendantPages(this.page.uniqueTitle, this.pages).length
      },
      copyIncludesSubPages () {
        return this.pageSubPageCount > 0 && this.copyPageWithSubPages
      },
      pageCopyPreview () {
        if (!this.page || !this.page.uniqueTitle) return null
        const plan = planPageCopy(this.page.uniqueTitle, this.pages, {
          includeSubPages: this.copyIncludesSubPages
        })
        return plan && plan.copies[0] ? plan.copies[0] : null
      },
      viewTransferDestinations () {
        if (!this.viewTransferView || !this.page) return []
        return pagesForViewTransfer({
          pages: this.pages,
          spreadsheets: this.spreadsheets,
          sourcePageUniqueTitle: this.page.uniqueTitle,
          view: this.viewTransferView,
          mode: this.viewTransferMode
        })
      },
      viewTransferPageOptions () {
        const current = this.page && this.page.uniqueTitle
        return this.viewTransferDestinations.map((page) => ({
          value: page.uniqueTitle,
          text: page.uniqueTitle === current
            ? `${page.title || page.uniqueTitle} (this page)`
            : (page.title || page.uniqueTitle)
        }))
      },
      viewTransferPlan () {
        if (!this.viewTransferView || !this.page) {
          return {
            ok: false,
            error: ``,
            titleError: ``,
            pageError: ``,
            uniqueTitle: ``,
            title: ``,
            filterByPageRecord: false,
            clearsPageRecord: false,
            relationshipTitle: ``
          }
        }
        return planViewTransfer({
          pages: this.pages,
          spreadsheets: this.spreadsheets,
          sourcePageUniqueTitle: this.page.uniqueTitle,
          viewUniqueTitle: this.viewTransferView.uniqueTitle,
          destinationPageUniqueTitle: this.viewTransferDestination,
          mode: this.viewTransferMode,
          uniqueTitle: this.viewTransferUniqueTitle
        })
      },
      viewTransferTitleState () {
        return this.viewTransferPlan.titleError ? false : null
      },
      viewTransferTitleError () {
        return this.viewTransferPlan.titleError || ``
      },
      viewTransferCanConfirm () {
        return this.viewTransferPlan.ok && !this.viewTransferSubmitting
      },
      newComponentSpreadSheetState () {
        if (!this.componentTypeRequiresSpreadsheet) return null
        if (this.newComponentSubmitAttempted && !this.newComponentSpreadSheet) return false
        return null
      },
      newComponentSpreadSheetFeedback () {
        if (!this.componentTypeRequiresSpreadsheet) return ''
        if (this.newComponentSubmitAttempted && !this.newComponentSpreadSheet) return 'a spreadsheet must be selected'
        return ''
      },
      newComponentTypeState () {
        if (this.newComponentSubmitAttempted && !this.newComponentType) return false
        return null
      },
      newComponentTypeFeedback () {
        if (this.newComponentSubmitAttempted && !this.newComponentType) return 'a component type must be selected'
        return ''
      },
      componentTypeSupportsAddAllFields () {
        return viewSupportsAddAllColumns(this.newComponentType)
      },
      componentTypeRequiresSpreadsheet () {
        return componentTypeNeedsSpreadsheet(this.newComponentType)
      },
      selectedSpreadsheetTitle () {
        const raw = this.newComponentSpreadSheet
        if (!raw) return ''
        const value = String(raw).replace(/::pageRecordFiltered$/, '')
        const option = this.spreadSheetOptions.find((opt) => opt.value === value)
        return option ? option.text : ''
      },
      loginSpreadSheets () {

        return loginSpreadSheetIndex(this.pages)
      },
      pageRequiresLogin () {

        return pageRequiresLoginFromAncestors(this.page, this.pages)
      },
      permissionEditorRules () {

        return (this.permissionTarget && this.permissionTarget.userPermissionRules) || { rules: [] }
      },
      appHasLogin () {

        const hasLogin = (components) => {

          if (!components) return false

          for (const comp of components) {

            if (comp.type === 'login' || comp.type === 'registration') return true
            if (comp.children && hasLogin(comp.children)) return true
            if (comp.components && hasLogin(comp.components)) return true
          }

          return false
        }

        return (this.pages || []).some(page => hasLogin(page.components))
      },
      pageRecordSpreadSheetUniqueTitle () {
        return findPageRecordSpreadSheet(this.pages, this.page)
      },
      enrichedSpreadSheetOptions () {
        const withLogin = (opt) => {

          if (!opt || !opt.value) return opt

          const value = String(opt.value).replace(/::pageRecordFiltered$/, '')
          const uses = this.loginSpreadSheets[value]

          if (!uses || !uses.length) return opt

          return { ...opt, text: `${opt.text} · login` }
        }
        const base = (this.spreadSheetOptions || []).map(withLogin)
        const pageRecordSSUT = this.pageRecordSpreadSheetUniqueTitle
        if (!pageRecordSSUT || !this.spreadsheets || !this.spreadsheets.length) {
          return base
        }

        const pageRecordSS = this.spreadsheets.find(s => s.uniqueTitle === pageRecordSSUT)
        if (!pageRecordSS) return base

        const pageRecordTitle = pageRecordSS.title || pageRecordSSUT
        const relatedSet = relatedSpreadSheetUniqueTitles(this.spreadsheets, pageRecordSSUT)
        const relatedOptions = []

        for (const opt of this.spreadSheetOptions) {
          if (relatedSet.has(opt.value)) {
            relatedOptions.push(withLogin({
              value: `${opt.value}::pageRecordFiltered`,
              text: `${opt.text} — related to page's ${pageRecordTitle} record`
            }))
          }
        }

        if (relatedOptions.length === 0) return base

        const separator = { value: '', text: '──────────', disabled: true }

        return [...relatedOptions, separator, ...base]
      }
    },
    methods: {
      showsSeparateId,
      sanitizeToUniqueTitle (title) {
        return title
          .toLowerCase()
          .replace(/[^a-z0-9\s_]/g, '')
          .replace(/\s+/g, '_')
          .replace(/_+/g, '_')
          .replace(/^_|_$/g, '')
      },
      getSpreadSheetTitle (uniqueTitle) {
        if (!uniqueTitle || !this.spreadsheets) return uniqueTitle
        const ss = this.spreadsheets.find(s => s.uniqueTitle === uniqueTitle)
        return ss ? ss.title : uniqueTitle
      },
      isLoginSpreadSheet (uniqueTitle) {

        const uses = uniqueTitle && this.loginSpreadSheets[uniqueTitle]

        return !!(uses && uses.length)
      },
      loginNoteForSpreadSheet (uniqueTitle) {

        return loginSpreadSheetNote(uniqueTitle && this.loginSpreadSheets[uniqueTitle])
      },
      getRelationshipColumnTitle (viewSpreadSheetUT) {
        const link = pageRecordRelationship(this.spreadsheets, viewSpreadSheetUT, this.pageRecordSpreadSheetUniqueTitle)
        return link ? link.columnTitle : null
      },
      rememberSavedPage (saved) {
        const synced = syncSavedPage(this.pages, saved)

        if (synced) this.page = synced
      },
      pageEditorsOpen () {

        const editors = [
          ['formEditRef', 'formEditVisible'],
          ['tableEditRef', 'tableEditVisible'],
          ['pivotEditRef', 'pivotEditVisible'],
          ['customEditRef', 'customEditVisible'],
          ['cardsEditRef', 'cardsEditVisible'],
          ['chartEditRef', 'chartEditVisible'],
          ['richtextEditRef', 'richtextEditVisible'],
          ['staticImageEditRef', 'staticImageEditVisible'],
          ['menuEditRef', 'menuEditVisible']
        ]

        for (const [refName, flag] of editors) {

          const editor = this.$refs[refName]

          if (editor && editor[flag]) return true
        }

        const css = this.$refs.viewCssEditorRef

        if (css && css.visible && css.mode !== 'app') return true

        return this.editPageModalVisible || this.createComponentModalVisible || this.permissionEditorVisible || this.pageOpenEditorVisible || this.viewTransferModalVisible || this.arrangementDirty
      },
      async refreshPageListOnly () {

        const current = this.page
        const pages = await this.$http.get(`${VUE_APP_PROXY_SERVER_URL}/pages/`, { headers: { 'application-unique-title': this.$route.params.application } })

        this.pages = pages.data || []

        if (current && current.uniqueTitle) {

          const index = this.pages.findIndex((page) => page.uniqueTitle === current.uniqueTitle)

          if (index >= 0) this.pages.splice(index, 1, current)
        }

        this.sidebarTree = pageTreeFromPages(this.pages)
      },
      async applyDesignerChange (change) {

        const pageId = this.page && this.page.uniqueTitle
        const keys = change && change.keys || []
        const appWide = keys.includes('application')
        const thisPage = pageId && (keys.includes(`page:${pageId}`) || appWide)
        const list = keys.includes('pages') || appWide
        const sheets = keys.some((key) => key === 'spreadsheets' || key.startsWith('spreadsheet:')) || appWide
        const appearance = keys.includes('settings:appearance') || keys.includes('custom-themes') || appWide
        const css = this.$refs.viewCssEditorRef

        if (!thisPage && !list && !sheets && !appearance) return

        if (this.arrangementDirty && (list || appWide)) {

          raiseDesignerWarning(change, 'pages')

          return
        }

        if (thisPage && this.pageEditorsOpen()) {

          raiseDesignerWarning(change, keys.includes(`page:${pageId}`) ? 'this page' : 'this application')

          return
        }

        if (appearance && css && css.visible && css.mode === 'app') {

          raiseDesignerWarning(change, 'app CSS')

          return
        }

        if (this.pageEditorsOpen()) {

          if (list) await this.refreshPageListOnly()

          return
        }

        if (thisPage || list || sheets || appearance) {

          this.editingPageUniqueTitle = ''
          await this.getPages()
        }
      },
      onPageUniqueTitleInput (val) {
        const value = (val && val.target) ? val.target.value : val
        this.newPageUniqueTitle = value
        this.newPageUniqueTitleManuallyEdited = !!value.trim()
      },
      onComponentTitleInput (val) {
        const value = (val && val.target) ? val.target.value : val
        this.newComponentTitleManuallyEdited = !!String(value || '').trim()
      },
      applyDefaultComponentTitle () {
        if (this.newComponentTitleManuallyEdited) return
        if (!this.componentTypeRequiresSpreadsheet) return

        const spreadsheetTitle = this.selectedSpreadsheetTitle
        const type = String(this.newComponentType || '').trim()
        if (!spreadsheetTitle || !type) return

        this.newComponentTitle = `${spreadsheetTitle} ${type}`
      },
      onComponentUniqueTitleInput (val) {
        const value = (val && val.target) ? val.target.value : val
        this.newComponentUniqueTitle = value
        this.newComponentUniqueTitleManuallyEdited = !!value.trim()
      },
      columnFormatLabel (column) {

        return formatColumnWidth(column && column.columnFormat)
      },
      columnCanvasStyle (column) {

        return columnDesignerStyle(column && column.columnFormat)
      },
      rowHasColumnWidth (row) {

        return ((row && row.components) || []).some((column) => this.columnFormatLabel(column))
      },
      openColumnFormat (column) {

        const format = columnFormatRecord(column && column.columnFormat)

        this.columnFormatTarget = column
        this.columnFormatWidth = format && format.width != null ? String(format.width) : ``
        this.columnFormatUnit = format && format.unit ? format.unit : `percent`
        this.columnFormatOverflow = format && format.overflow === `mobile` ? `mobile` : `scroll`
        this.columnFormatError = ``
        this.columnFormatModalVisible = true
      },
      saveColumnFormat (event) {

        const raw = String(this.columnFormatWidth || ``).trim()
        const problem = columnFormatProblem(raw ? { width: raw, unit: this.columnFormatUnit } : null)

        if (problem) {

          if (event && event.preventDefault) event.preventDefault()

          this.columnFormatError = problem
          return
        }

        if (!this.columnFormatTarget) return

        const record = columnFormatRecord({
          width: raw,
          unit: this.columnFormatUnit,
          overflow: this.columnFormatOverflow
        })

        if (record) this.columnFormatTarget.columnFormat = record
        else delete this.columnFormatTarget.columnFormat

        this.save()
      },
      addColumn (component) {

        console.log(`add column!`)

        component.components.push({
          type: `column`,
          components: []
        })

        this.save()
      },
      addRow (component) {

        console.log(`add row!`)

        component.components.push({
          type: `row`,
          components: [{
            type: `column`,
            components: []
          }]
        })

        this.save()
      },
      removeComponent (parentComponent, removeComponent) {

        parentComponent.components = _.filter(parentComponent.components, (component) => {

          return component !== removeComponent
        })

        this.save()
      },
      async toggleRequiresLogin () {

        if (ancestorRequiresLogin(this.page, this.pages)) return

        this.page.requiresLogin = !this.page.requiresLogin

        // update the sidebar page object so the icon changes immediately
        const sidebarPage = this.pages.find(p => p.uniqueTitle === this.page.uniqueTitle)

        if (sidebarPage) {

          sidebarPage.requiresLogin = this.page.requiresLogin
        }

        await this.save()
      },
      showViewLoginControls (view) {

        const pageBlocksControls = !this.page || !!this.page.requiresLogin || pageIsInLoginProtectedStructure(this.page, this.pages)

        return shouldOfferViewLoginControls(pageBlocksControls, this.appHasLogin, view)
      },
      showUserPermissionButton () {

        return this.appHasLogin
      },
      hasUserPermissionRules (target) {

        return hasUserPermissionRules(target)
      },
      hasPageOpenRules (page) {

        return hasPageOpenRules(page)
      },
      pageOpenTip,
      openPageOpenRules () {

        this.pageOpenEditorVisible = true
      },
      savePageOpenRules ({ rules }) {

        if (!this.page) return

        this.page.pageOpenRules = rules
        this.pageOpenEditorVisible = false
        this.save()
      },
      openUserPermissionRules (target, subject) {

        this.permissionTarget = target
        this.permissionSubject = subject
        this.permissionEditorVisible = true
      },
      saveUserPermissionRules (rules) {

        if (!this.permissionTarget) return

        this.permissionTarget.userPermissionRules = rules
        this.permissionEditorVisible = false
        this.save()
      },
      defaultValueRuleBadge (view) {

        return defaultValueRuleBadge(view)
      },
      viewIconTip,
      viewLoginTip,
      pageLoginTip (page) {

        return pageLoginTipText(page, this.pages)
      },
      userPermissionTip,
      viewHideWhenLoggedInTip,
      menuItemSummary (view) {

        const labels = (view && view.menuItems || []).map(item => item && item.label).filter(Boolean)

        if (!labels.length) return 'no items'

        return labels.join(', ')
      },
      viewSpreadsheetTip (view) {

        return viewSpreadsheetTip(this.getSpreadSheetTitle(view && view.spreadSheet), {
          filterByPageRecord: !!(view && view.filterByPageRecord),
          relationshipTitle: view ? this.getRelationshipColumnTitle(view.spreadSheet) : ''
        })
      },
      toggleViewLoginFlag (view, flag) {

        const next = nextViewLoginFlags(view, flag)

        view.requiresLogin = next.requiresLogin
        view.hideWhenLoggedIn = next.hideWhenLoggedIn

        this.save()
      },
      async save () {

        if (!this.page || !this.page.uniqueTitle) {

          return
        }

        let updatedPage
        const tempPage = _.cloneDeep(this.page)

        delete tempPage.children

        console.log(`attempt to saave!`)
        console.log(tempPage)

        try {

          updatedPage = await this.$http.put(`${VUE_APP_PROXY_SERVER_URL}/pages/${this.page.uniqueTitle}`, tempPage, {headers: {'application-unique-title': this.$route.params.application}})
        } catch (error) {

          console.log(`error saving page:`)
          console.log(error)
        }

        if (!updatedPage || !updatedPage.data || typeof updatedPage.data !== `object`) {

          return
        }

        updatedPage.data.children = this.page.children

        this.page = updatedPage.data
        this.rememberSavedPage(this.page)
        this.sendPageToPreview()
      },
      async getSpreadSheets () {

        const spreadsheets = await this.$http.get(`${VUE_APP_PROXY_SERVER_URL}/spreadsheets/`, {headers: {'application-unique-title': this.$route.params.application}})

        console.log(`have spreadsheets!`)
        console.log(spreadsheets)

        this.spreadsheets = spreadsheets.data

        this.spreadSheetOptions = this.spreadsheets.map((spreadsheet) => {

          return {
            value: spreadsheet.uniqueTitle,
            text: spreadsheet.title
          }
        })
      },
      async loadAppCss () {
        try {
          const res = await this.$http.get(`${VUE_APP_PROXY_SERVER_URL}/settings`, { headers: { 'application-unique-title': this.$route.params.application } })
          this.appCustomCss = (res.data && res.data.customCss) || ''
        } catch (err) {
          console.log('failed to load app custom css', err)
        }
      },
      async getPages () {

        await this.getSpreadSheets()
        await this.loadAppCss()

        const pages = await this.$http.get(`${VUE_APP_PROXY_SERVER_URL}/pages/`, {headers: {'application-unique-title': this.$route.params.application}})

        console.log(`got pages!`)
        console.log(pages)

        this.pages = pages.data
        this.sidebarTree = pageTreeFromPages(this.pages)
        const placement = pagePlacementFromTree(this.sidebarTree)
        this.lastPageArrangement = placement.pages ? JSON.stringify(placement.pages) : ``

        if (this.editingPageUniqueTitle === ``) {

          const targetUniqueTitle = this.$route.params.page
          const target = targetUniqueTitle
            ? this.pages.find(p => p.uniqueTitle === targetUniqueTitle)
            : null
          this.page = target || this.pages[0] || { components: [] }
          if (this.page) {

            this.rememberPage(this.page)
            this.$emit('page-changed', this.page)
          }
        }
      },
      pageDragFilter (evt, target, sortable) {
        const block = pageNestDragFilter(evt, target, sortable)
        if (!block) this.preparePageDrag(target)
        return block
      },
      preparePageDrag (item) {
        this._pageDragItem = item
        this._pageDragLive = true
        this.armPageDragDismiss()
      },
      onPageDragStart (evt) {
        if (!this._pageDragLive) return
        this.showPageDropZones(evt)
      },
      showPageDropZones (evt) {
        const sidebar = this.$el && this.$el.querySelector('.sidebar')
        if (sidebar) sidebar.classList.add('page-dragging')
        this.markAllowedPageDrops(evt)
      },
      armPageDragDismiss () {
        if (this._pageDragDismiss) return
        let dragStarted = false
        let dismissTimer = null
        const dismissIfNotDragging = () => {
          if (dragStarted) return
          clearTimeout(dismissTimer)
          dismissTimer = setTimeout(() => {
            if (dragStarted || !this._pageDragLive) return
            this.clearPageDrag()
          }, 0)
        }
        const markDragStarted = () => {
          dragStarted = true
          clearTimeout(dismissTimer)
        }
        const dismissAfterDrop = () => {
          clearTimeout(dismissTimer)
          dismissTimer = setTimeout(() => this.clearPageDrag(), 0)
        }
        window.addEventListener('pointerup', dismissIfNotDragging, false)
        window.addEventListener('mouseup', dismissIfNotDragging, false)
        window.addEventListener('touchend', dismissIfNotDragging, false)
        window.addEventListener('dragstart', markDragStarted, true)
        window.addEventListener('dragend', dismissAfterDrop, false)
        this._pageDragDismiss = () => {
          clearTimeout(dismissTimer)
          window.removeEventListener('pointerup', dismissIfNotDragging, false)
          window.removeEventListener('mouseup', dismissIfNotDragging, false)
          window.removeEventListener('touchend', dismissIfNotDragging, false)
          window.removeEventListener('dragstart', markDragStarted, true)
          window.removeEventListener('dragend', dismissAfterDrop, false)
        }
      },
      markAllowedPageDrops (evt) {
        const sidebar = this.$el && this.$el.querySelector('.sidebar')
        if (!sidebar) return
        const title = evt && evt.item && evt.item.getAttribute('data-page-title')
        const dragged = title ? (this.pages || []).find(page => page.uniqueTitle === title) : null
        sidebar.querySelectorAll('.page-nest-drop').forEach((drop) => {
          const parentTitle = drop.getAttribute('data-page-parent') || ''
          const parent = parentTitle
            ? (this.pages || []).find(page => page.uniqueTitle === parentTitle)
            : null
          const allowed = !dragged || canPlacePageUnder(dragged, parent, this.pages, this.spreadsheets)
          drop.classList.toggle('page-nest-allowed', allowed)
        })
        this.showRootDrop = !dragged || canPlacePageUnder(dragged, null, this.pages, this.spreadsheets)
      },
      canMovePage (evt) {
        if (!evt || !evt.dragged || !evt.to) return true
        if (evt.dragged.contains(evt.to)) return false
        const dragged = evt.draggedContext && evt.draggedContext.element
        const parentTitle = evt.to.getAttribute('data-page-parent') || ''
        const parent = parentTitle
          ? (this.pages || []).find(page => page.uniqueTitle === parentTitle)
          : null
        return canPlacePageUnder(dragged, parent, this.pages, this.spreadsheets)
      },
      clearPageDrag () {
        this._pageDragLive = false
        this.showRootDrop = false
        if (this._pageDragDismiss) {
          this._pageDragDismiss()
          this._pageDragDismiss = null
        }
        const sidebar = this.$el && this.$el.querySelector('.sidebar')
        if (!sidebar) return
        sidebar.classList.remove('page-dragging')
        sidebar.querySelectorAll('.page-nest-allowed').forEach(drop => drop.classList.remove('page-nest-allowed'))
      },
      onPageDragEnd () {
        this.clearPageDrag()
        this.arrangementDirty = true
        if (this.arrangingPages) return
        this.flushPageArrangement()
      },
      movePageByKey (page, direction) {

        if (!page || !movePageInTree(this.sidebarTree, page.uniqueTitle, direction)) return

        this.onPageDragEnd()
        this.focusReorder(page.uniqueTitle)
      },
      moveViewByKey (view, direction) {

        const found = this.findViewList(view && view.uniqueTitle)

        if (!found || !moveListItem(found.list, found.index, direction)) return

        this.onViewDragEnd()
        this.focusReorder(view.uniqueTitle)
      },
      findViewList (uniqueTitle) {

        const rows = (this.page && this.page.components) || []

        for (const row of rows) {

          for (const column of row.components || []) {

            const list = column.components || []
            const index = list.findIndex(item => item && item.uniqueTitle === uniqueTitle)

            if (index >= 0) return { list, index }
          }
        }

        return null
      },
      focusReorder (id) {

        this.$nextTick(() => {

          const root = this.$el
          if (!root || !id) return

          const handle = root.querySelector(`[data-reorder-id="${CSS.escape(id)}"]`)

          if (handle) handle.focus()
        })
      },
      async flushPageArrangement () {
        this.arrangingPages = true

        try {
          while (this.arrangementDirty) {
            this.arrangementDirty = false
            await this.persistPageArrangement()
          }
        } finally {
          this.arrangingPages = false
        }
      },
      async persistPageArrangement () {
        await this.$nextTick()

        const placement = pagePlacementFromTree(this.sidebarTree)

        if (placement.error) {
          console.error(placement.error)
          await this.getPages()
          return
        }

        const normalized = normalizePageArrangement(
          this.pages.map(page => page.uniqueTitle),
          placement.pages
        )

        if (normalized.error) {
          console.error(normalized.error)
          await this.getPages()
          return
        }

        const arrangementKey = JSON.stringify(normalized.pages)

        if (arrangementKey === this.lastPageArrangement) return

        applyPageParents(this.pages, normalized.pages)

        try {
          await this.$http.put(`${VUE_APP_PROXY_SERVER_URL}/pages/arrange`, {
            pages: normalized.pages
          }, {
            headers: { 'application-unique-title': this.$route.params.application }
          })
          this.lastPageArrangement = arrangementKey
        } catch (error) {
          console.error('Failed to move pages:', error)
          await this.getPages()
        }
      },
      onViewDragEnd () {
        this.save()
      },
      showCreatePage () {

        this.newPageTitle = ``
        this.newPageUniqueTitle = ``
        this.newPageUniqueTitleManuallyEdited = false
        this.newPageSubmitAttempted = false
        this.createPageActiveTab = 0
        this.aiPagePrompt = ``
        this.aiPageError = null
        this.createPageAiSubmitting = false

        this.createPageModalVisible = true
      },
      onCreatePageModalOk (event) {

        if (this.createPageActiveTab === 1) {

          return this.createPageWithAi(event)
        }

        return this.createPage(event)
      },
      async createPageWithAi (event) {

        event.preventDefault()

        const prompt = (this.aiPagePrompt || ``).trim()

        if (!prompt.length) {

          this.aiPageError = `Please describe the page you want to create.`
          return
        }

        this.aiPageError = null
        this.createPageAiSubmitting = true

        try {

          const response = await this.$http.post(
            `${VUE_APP_PROXY_SERVER_URL}/ai-pages/create`,
            { prompt },
            {
              headers: {
                'Content-Type': 'application/json',
                'application-unique-title': this.$route.params.application
              },
              timeout: 600000
            }
          )

          const created = response && response.data && response.data.page

          this.createPageModalVisible = false
          this.aiPagePrompt = ``
          await this.getPages()

          if (created) {

            const fresh = this.pages.find((page) => page.uniqueTitle === created.uniqueTitle)

            this.loadPage(fresh || created)
          }
        } catch (error) {

          console.error(`AI create page failed:`, error)
          const data = error && error.response && error.response.data
          this.aiPageError = (typeof data === 'string' && data) || (error && error.message) || `AI request failed`
        }

        this.createPageAiSubmitting = false
      },
      showAddComponent (component) {

        this.addComponentParentComponent = component

        this.editingPageUniqueTitle = this.page.title

        this.newComponentTitleManuallyEdited = false
        this.newComponentUniqueTitleManuallyEdited = false
        this.newComponentTitle = ``
        this.newComponentUniqueTitle = ``
        this.newComponentSpreadSheet = ``
        this.newComponentType = ``
        this.newComponentSubmitAttempted = false
        this.addAllFieldsFromSpreadsheet = false

        this.createComponentModalVisible = true
      },
      showCssEditor (component) {
        this.activeComponent = component
        this.$refs.viewCssEditorRef.open(component, this)
      },
      sendCssToPreview (componentUniqueTitle, cssText) {
        const iframe = this.$refs.previewIframe
        if (!iframe || !iframe.contentWindow) return

        iframe.contentWindow.postMessage({
          type: 'superturbo-preview-css',
          componentUniqueTitle: componentUniqueTitle,
          css: cssText
        }, '*')
      },
      saveComponentCss (componentUniqueTitle, cssText) {
        const found = this.findComponentByUniqueTitle(this.page, componentUniqueTitle)
        if (found) {
          found.customCss = cssText
        }
        this.save()
      },
      showPageCssEditor () {
        this.$refs.viewCssEditorRef.open(this.page, this, 'page')
      },
      sendPageCssToPreview (cssText) {
        const iframe = this.$refs.previewIframe
        if (!iframe || !iframe.contentWindow) return

        iframe.contentWindow.postMessage({
          type: 'superturbo-preview-page-css',
          pageUniqueTitle: this.page.uniqueTitle,
          css: cssText
        }, '*')
      },
      savePageCss (cssText) {
        this.page.customCss = cssText
        this.save()
      },
      showAppCssEditor () {
        this.$refs.viewCssEditorRef.open({ customCss: this.appCustomCss }, this, 'app')
      },
      sendAppCssToPreview (cssText) {
        const iframe = this.$refs.previewIframe
        if (!iframe || !iframe.contentWindow) return

        iframe.contentWindow.postMessage({
          type: 'superturbo-preview-app-css',
          css: cssText
        }, '*')
      },
      async saveAppCss (cssText) {
        this.appCustomCss = cssText
        try {
          await this.$http.put(`${VUE_APP_PROXY_SERVER_URL}/settings`, { customCss: cssText }, { headers: { 'application-unique-title': this.$route.params.application } })
        } catch (err) {
          console.log('failed to save app custom css', err)
        }
        this.sendAppCssToPreview(cssText)
      },
      findComponentByUniqueTitle (node, uniqueTitle) {
        if (!node || !node.components) return null
        for (const comp of node.components) {
          if (comp.uniqueTitle === uniqueTitle) return comp
          if (comp.components) {
            const found = this.findComponentByUniqueTitle(comp, uniqueTitle)
            if (found) return found
          }
        }
        return null
      },
      showEditComponent (component) {

        this.editingPageUniqueTitle = this.page.title

        console.log('i am here!!!!!')
        console.log(this.$children)
        console.log(this)
        console.log(this.$refs.formEditRef)
        console.log(component)
        console.log('.....')

        this.activeComponent = component

        switch (component.type) {

          case `form`:
          case `login`:
          case `registration`:

            this.$refs.formEditRef.parentDesigner = this
            this.$refs.formEditRef.allSpreadsheets = this.spreadsheets || []
            this.$refs.formEditRef.title = component.title
            this.$refs.formEditRef.uniqueTitle = component.uniqueTitle
            this.$refs.formEditRef.spreadSheetUniqueTitle = component.spreadSheet
            this.$refs.formEditRef.filterByPageRecord = !!component.filterByPageRecord
            this.$refs.formEditRef.pageRecordSpreadSheetUniqueTitle = this.pageRecordSpreadSheetUniqueTitle || ''
            this.$refs.formEditRef.applicationUniqueTitle = this.$route.params.application || ''
            this.$refs.formEditRef.showEditFormComponent(component.components || [], component.spreadSheet, {
              submissionRules: component.submissionRules,
              defaultValueRules: component.defaultValueRules,
              formActions: component.formActions,
              pages: this.pages || [],
              pageComponents: this.page && this.page.components ? this.page.components : [],
              currentPageUniqueTitle: this.page ? this.page.uniqueTitle : '',
              componentType: component.type,
              twoFactor: component.twoFactor
            })

            return
          case `table`:

            console.log(`w0wza we abvout to fail`)
            console.log(this.$refs.tableEditRef)

            this.$refs.tableEditRef.parentDesigner = this
            this.$refs.tableEditRef.uniqueTitle = component.uniqueTitle
            this.$refs.tableEditRef.title = component.title
            this.$refs.tableEditRef.spreadSheetUniqueTitle = component.spreadSheet
            this.$refs.tableEditRef.realtime = !!component.realtime
            this.$refs.tableEditRef.csvExport = !!component.csvExport
            this.$refs.tableEditRef.pageSize = component.pageSize || ''
            this.$refs.tableEditRef.pageSizeMax = component.pageSizeMax || ''
            this.$refs.tableEditRef.wrapHeaders = !!component.wrapHeaders
            this.$refs.tableEditRef.hideEmptyColumns = !!component.hideEmptyColumns
            this.$refs.tableEditRef.filterByPageRecord = !!component.filterByPageRecord
            this.$refs.tableEditRef.pageRecordSpreadSheetUniqueTitle = this.pageRecordSpreadSheetUniqueTitle || ''
            this.$refs.tableEditRef.relationshipColumnTitle = component.filterByPageRecord ? (this.getRelationshipColumnTitle(component.spreadSheet) || '') : ''
            this.$refs.tableEditRef.userFiltering = component.userFiltering
            this.$refs.tableEditRef.searchEnabled = component.searchEnabled === true || component.searchEnabled === 'true'
            this.$refs.tableEditRef.searchScope = component.searchScope === 'spreadsheet' ? 'spreadsheet' : 'view'
            this.$refs.tableEditRef.restrictUserFiltering = component.restrictUserFiltering
            this.$refs.tableEditRef.defaultUserFilters = component.defaultUserFilters || []
            this.$refs.tableEditRef.defaultUserFilterMatchMode = component.defaultUserFilterMatchMode || 'all'
            this.$refs.tableEditRef.preFilters = component.preFilters || []
            this.$refs.tableEditRef.preFilterMatchMode = component.preFilterMatchMode || 'all'
            this.$refs.tableEditRef.filterButtons = _.cloneDeep(component.filterButtons || [])
            this.$refs.tableEditRef.allowMultipleFilterButtons = component.allowMultipleFilterButtons === true || component.allowMultipleFilterButtons === 'true'
            this.$refs.tableEditRef.sorts = _.cloneDeep(component.sorts || [])
            this.$refs.tableEditRef.disableUserSorting = !!component.disableUserSorting
            this.$refs.tableEditRef.filters = component.filters || []
            this.$refs.tableEditRef.allSpreadsheets = this.spreadsheets || []
            this.$refs.tableEditRef.applicationUniqueTitle = this.$route.params.application || ''
            this.$refs.tableEditRef.pages = this.pages || []
            this.$refs.tableEditRef.pageComponents = this.page && this.page.components ? this.page.components : []
            this.$refs.tableEditRef.currentPageUniqueTitle = this.page ? this.page.uniqueTitle : ''
            this.$refs.tableEditRef.showEditTableComponent(component.components || [], component.spreadSheet)

            return
          case `pivot`:

            this.$refs.pivotEditRef.parentDesigner = this
            this.$refs.pivotEditRef.uniqueTitle = component.uniqueTitle
            this.$refs.pivotEditRef.title = component.title
            this.$refs.pivotEditRef.spreadSheetUniqueTitle = component.spreadSheet
            this.$refs.pivotEditRef.realtime = !!component.realtime
            this.$refs.pivotEditRef.filterByPageRecord = !!component.filterByPageRecord
            this.$refs.pivotEditRef.pageRecordSpreadSheetUniqueTitle = this.pageRecordSpreadSheetUniqueTitle || ''
            this.$refs.pivotEditRef.relationshipColumnTitle = component.filterByPageRecord ? (this.getRelationshipColumnTitle(component.spreadSheet) || '') : ''
            this.$refs.pivotEditRef.userFiltering = component.userFiltering
            this.$refs.pivotEditRef.searchEnabled = component.searchEnabled === true || component.searchEnabled === 'true'
            this.$refs.pivotEditRef.searchScope = component.searchScope === 'spreadsheet' ? 'spreadsheet' : 'view'
            this.$refs.pivotEditRef.restrictUserFiltering = component.restrictUserFiltering
            this.$refs.pivotEditRef.defaultUserFilters = component.defaultUserFilters || []
            this.$refs.pivotEditRef.defaultUserFilterMatchMode = component.defaultUserFilterMatchMode || 'all'
            this.$refs.pivotEditRef.preFilters = component.preFilters || []
            this.$refs.pivotEditRef.preFilterMatchMode = component.preFilterMatchMode || 'all'
            this.$refs.pivotEditRef.filterButtons = _.cloneDeep(component.filterButtons || [])
            this.$refs.pivotEditRef.allowMultipleFilterButtons = component.allowMultipleFilterButtons === true || component.allowMultipleFilterButtons === 'true'
            this.$refs.pivotEditRef.rowFields = _.cloneDeep(component.rowFields || [])
            this.$refs.pivotEditRef.columnFields = _.cloneDeep(component.columnFields || [])
            this.$refs.pivotEditRef.valueFields = _.cloneDeep(component.valueFields || [])
            this.$refs.pivotEditRef.showSubtotals = component.showSubtotals !== false && component.showSubtotals !== 'false'
            this.$refs.pivotEditRef.showRowGrandTotal = component.showRowGrandTotal !== false && component.showRowGrandTotal !== 'false'
            this.$refs.pivotEditRef.showColumnGrandTotal = component.showColumnGrandTotal !== false && component.showColumnGrandTotal !== 'false'
            this.$refs.pivotEditRef.allSpreadsheets = this.spreadsheets || []
            this.$refs.pivotEditRef.applicationUniqueTitle = this.$route.params.application || ''
            this.$refs.pivotEditRef.showEditPivotComponent()

            return
          case `custom`:

            this.$refs.customEditRef.parentDesigner = this
            this.$refs.customEditRef.uniqueTitle = component.uniqueTitle
            this.$refs.customEditRef.title = component.title
            this.$refs.customEditRef.spreadSheetUniqueTitle = component.spreadSheet
            this.$refs.customEditRef.viewCode = component.viewCode || ''
            this.$refs.customEditRef.viewCss = component.viewCss || ''
            this.$refs.customEditRef.exampleId = component.exampleId || ''
            this.$refs.customEditRef.realtime = !!component.realtime
            this.$refs.customEditRef.filterByPageRecord = !!component.filterByPageRecord
            this.$refs.customEditRef.pageRecordSpreadSheetUniqueTitle = this.pageRecordSpreadSheetUniqueTitle || ''
            this.$refs.customEditRef.relationshipColumnTitle = component.filterByPageRecord ? (this.getRelationshipColumnTitle(component.spreadSheet) || '') : ''
            this.$refs.customEditRef.userFiltering = component.userFiltering
            this.$refs.customEditRef.searchEnabled = component.searchEnabled === true || component.searchEnabled === 'true'
            this.$refs.customEditRef.searchScope = component.searchScope === 'spreadsheet' ? 'spreadsheet' : 'view'
            this.$refs.customEditRef.restrictUserFiltering = component.restrictUserFiltering
            this.$refs.customEditRef.defaultUserFilters = _.cloneDeep(component.defaultUserFilters || [])
            this.$refs.customEditRef.defaultUserFilterMatchMode = component.defaultUserFilterMatchMode || 'all'
            this.$refs.customEditRef.preFilters = _.cloneDeep(component.preFilters || [])
            this.$refs.customEditRef.preFilterMatchMode = component.preFilterMatchMode || 'all'
            this.$refs.customEditRef.filterButtons = _.cloneDeep(component.filterButtons || [])
            this.$refs.customEditRef.allowMultipleFilterButtons = component.allowMultipleFilterButtons === true || component.allowMultipleFilterButtons === 'true'
            this.$refs.customEditRef.allSpreadsheets = this.spreadsheets || []
            this.$refs.customEditRef.applicationUniqueTitle = this.$route.params.application || ''
            this.$refs.customEditRef.showEditCustomComponent()

            return
          case `cards`:
          case `card`:
          case `maps`:
          case `calendar`:

            this.$refs.cardsEditRef.isMap = component.type === 'maps'
            this.$refs.cardsEditRef.addressColumn = component.type === 'maps' ? (component.addressColumn || '') : ''
            this.$refs.cardsEditRef.latitudeColumn = component.type === 'maps' ? (component.latitudeColumn || '') : ''
            this.$refs.cardsEditRef.longitudeColumn = component.type === 'maps' ? (component.longitudeColumn || '') : ''
            this.$refs.cardsEditRef.mapStyleUrl = component.type === 'maps' ? (component.mapStyleUrl || '') : ''
            this.$refs.cardsEditRef.isDetail = component.type === 'card'
            this.$refs.cardsEditRef.isCalendar = component.type === 'calendar'
            this.$refs.cardsEditRef.dateColumn = component.type === 'calendar' ? (component.dateColumn || '') : ''
            this.$refs.cardsEditRef.titleColumn = component.type === 'calendar' ? (component.titleColumn || '') : ''
            this.$refs.cardsEditRef.descriptionColumn = component.type === 'calendar' ? (component.descriptionColumn || '') : ''
            this.$refs.cardsEditRef.recurrenceColumn = component.type === 'calendar' ? (component.recurrenceColumn || '') : ''
            this.$refs.cardsEditRef.calendarLayout = component.type === 'calendar' ? (component.calendarLayout || 'month') : 'month'
            this.$refs.cardsEditRef.addFormUniqueTitle = component.type === 'calendar' ? (component.addFormUniqueTitle || '') : ''
            this.$refs.cardsEditRef.editorTitle = component.type === 'maps' ? 'edit maps' : (component.type === 'calendar' ? 'edit calendar' : (component.type === 'card' ? 'edit card' : 'edit cards'))
            this.$refs.cardsEditRef.parentDesigner = this
            this.$refs.cardsEditRef.uniqueTitle = component.uniqueTitle
            this.$refs.cardsEditRef.title = component.title
            this.$refs.cardsEditRef.spreadSheetUniqueTitle = component.spreadSheet
            this.$refs.cardsEditRef.realtime = !!component.realtime
            const cardsAcrossCount = component.type === 'cards' ? cardsAcross(component) : null
            this.$refs.cardsEditRef.cardsPerRow = cardsAcrossCount ? String(cardsAcrossCount) : ''
            this.$refs.cardsEditRef.filterByPageRecord = !!component.filterByPageRecord
            this.$refs.cardsEditRef.pageRecordSpreadSheetUniqueTitle = this.pageRecordSpreadSheetUniqueTitle || ''
            this.$refs.cardsEditRef.relationshipColumnTitle = component.filterByPageRecord ? (this.getRelationshipColumnTitle(component.spreadSheet) || '') : ''
            this.$refs.cardsEditRef.userFiltering = component.userFiltering
            this.$refs.cardsEditRef.searchEnabled = component.searchEnabled === true || component.searchEnabled === 'true'
            this.$refs.cardsEditRef.searchScope = component.searchScope === 'spreadsheet' ? 'spreadsheet' : 'view'
            this.$refs.cardsEditRef.restrictUserFiltering = component.restrictUserFiltering
            this.$refs.cardsEditRef.defaultUserFilters = component.defaultUserFilters || []
            this.$refs.cardsEditRef.defaultUserFilterMatchMode = component.defaultUserFilterMatchMode || 'all'
            this.$refs.cardsEditRef.preFilters = component.preFilters || []
            this.$refs.cardsEditRef.preFilterMatchMode = component.preFilterMatchMode || 'all'
            this.$refs.cardsEditRef.filterButtons = _.cloneDeep(component.filterButtons || [])
            this.$refs.cardsEditRef.allowMultipleFilterButtons = component.allowMultipleFilterButtons === true || component.allowMultipleFilterButtons === 'true'
            this.$refs.cardsEditRef.sorts = _.cloneDeep(component.sorts || [])
            this.$refs.cardsEditRef.allSpreadsheets = this.spreadsheets || []
            this.$refs.cardsEditRef.applicationUniqueTitle = this.$route.params.application || ''
            this.$refs.cardsEditRef.pages = this.pages || []
            this.$refs.cardsEditRef.pageComponents = this.page && this.page.components ? this.page.components : []
            this.$refs.cardsEditRef.currentPageUniqueTitle = this.page ? this.page.uniqueTitle : ''
            this.$refs.cardsEditRef.showEditCardsComponent(component.components || [], component.spreadSheet)

            return
          case `chart`:

            this.$refs.chartEditRef.parentDesigner = this
            this.$refs.chartEditRef.uniqueTitle = component.uniqueTitle
            this.$refs.chartEditRef.title = component.title
            this.$refs.chartEditRef.spreadSheetUniqueTitle = component.spreadSheet
            this.$refs.chartEditRef.realtime = !!component.realtime
            this.$refs.chartEditRef.filterByPageRecord = !!component.filterByPageRecord
            this.$refs.chartEditRef.pageRecordSpreadSheetUniqueTitle = this.pageRecordSpreadSheetUniqueTitle || ''
            this.$refs.chartEditRef.relationshipColumnTitle = component.filterByPageRecord ? (this.getRelationshipColumnTitle(component.spreadSheet) || '') : ''
            this.$refs.chartEditRef.chartType = component.chartType || 'bar'
            this.$refs.chartEditRef.labelField = component.labelField || ''
            this.$refs.chartEditRef.labelFieldLabel = component.labelFieldLabel || ''
            this.$refs.chartEditRef.valueFields = component.valueFields && component.valueFields.length > 0
              ? [...component.valueFields]
              : (component.valueField ? [component.valueField] : [''])
            this.$refs.chartEditRef.valueFieldLabels = component.valueFieldLabels && component.valueFieldLabels.length > 0
              ? [...component.valueFieldLabels]
              : this.$refs.chartEditRef.valueFields.map(() => '')
            this.$refs.chartEditRef.valueFieldTrendLines = component.valueFieldTrendLines && component.valueFieldTrendLines.length > 0
              ? [...component.valueFieldTrendLines]
              : this.$refs.chartEditRef.valueFields.map(() => false)
            this.$refs.chartEditRef.aggregation = component.aggregation || 'sum'
            this.$refs.chartEditRef.userFiltering = component.userFiltering
            this.$refs.chartEditRef.restrictUserFiltering = component.restrictUserFiltering
            this.$refs.chartEditRef.defaultUserFilters = component.defaultUserFilters || []
            this.$refs.chartEditRef.defaultUserFilterMatchMode = component.defaultUserFilterMatchMode || 'all'
            this.$refs.chartEditRef.preFilters = component.preFilters || []
            this.$refs.chartEditRef.preFilterMatchMode = component.preFilterMatchMode || 'all'
            this.$refs.chartEditRef.filterButtons = _.cloneDeep(component.filterButtons || [])
            this.$refs.chartEditRef.allowMultipleFilterButtons = component.allowMultipleFilterButtons === true || component.allowMultipleFilterButtons === 'true'
            this.$refs.chartEditRef.sorts = _.cloneDeep(component.sorts || [])
            this.$refs.chartEditRef.allSpreadsheets = this.spreadsheets || []
            this.$refs.chartEditRef.applicationUniqueTitle = this.$route.params.application || ''
            this.$refs.chartEditRef.showEditChartComponent(component.spreadSheet)

            return
          case `richtext`:

            this.$refs.richtextEditRef.parentDesigner = this
            this.$refs.richtextEditRef.uniqueTitle = component.uniqueTitle
            this.$refs.richtextEditRef.title = component.title
            this.$refs.richtextEditRef.richTextContent = component.richTextContent || ''
            this.$refs.richtextEditRef.showEditRichtextComponent()

            return
          case `staticimage`:

            this.$refs.staticImageEditRef.parentDesigner = this
            this.$refs.staticImageEditRef.uniqueTitle = component.uniqueTitle
            this.$refs.staticImageEditRef.title = component.title
            this.$refs.staticImageEditRef.staticImageId = component.staticImageId || null
            this.$refs.staticImageEditRef.altText = component.altText || ''
            this.$refs.staticImageEditRef.showEditStaticImageComponent()

            return
          case `menu`:

            this.$refs.menuEditRef.parentDesigner = this
            this.$refs.menuEditRef.uniqueTitle = component.uniqueTitle
            this.$refs.menuEditRef.title = component.title
            this.$refs.menuEditRef.menuItems = _.cloneDeep(component.menuItems || [])
            this.$refs.menuEditRef.pages = this.pages || []
            this.$refs.menuEditRef.spreadsheets = this.spreadsheets || []
            this.$refs.menuEditRef.currentPageUniqueTitle = this.page ? this.page.uniqueTitle : ''
            this.$refs.menuEditRef.applicationUniqueTitle = this.$route.params.application || ''
            this.$refs.menuEditRef.showEditMenuComponent()

            return
        }
      },
      saveActiveComponent (component) {

        console.log('COMPONENT')
        console.log(component)

        this.activeComponent.components = _.cloneDeep(component.components)
        this.activeComponent.title = component.title
        this.activeComponent.uniqueTitle = component.uniqueTitle
        this.activeComponent.spreadSheetUniqueTitle = component.spreadSheetUniqueTitle
        this.activeComponent.userFiltering = component.userFiltering
        if (component.searchEnabled !== undefined) {
          this.activeComponent.searchEnabled = !!component.searchEnabled
          this.activeComponent.searchScope = component.searchScope === 'spreadsheet' ? 'spreadsheet' : 'view'
        }
        this.activeComponent.restrictUserFiltering = component.restrictUserFiltering
        this.activeComponent.defaultUserFilters = component.defaultUserFilters
        this.activeComponent.defaultUserFilterMatchMode = component.defaultUserFilterMatchMode
        this.activeComponent.preFilters = component.preFilters
        this.activeComponent.preFilterMatchMode = component.preFilterMatchMode
        if (Array.isArray(component.filterButtons)) {
          const filterButtons = normalizeFilterButtons(component.filterButtons)
          if (filterButtons.length) this.activeComponent.filterButtons = filterButtons
          else delete this.activeComponent.filterButtons
        }
        if (component.allowMultipleFilterButtons !== undefined) {
          this.activeComponent.allowMultipleFilterButtons = !!component.allowMultipleFilterButtons
        }
        if (component.disableUserSorting !== undefined) {
          this.activeComponent.disableUserSorting = !!component.disableUserSorting
        }
        if (Array.isArray(component.sorts)) {
          const sorts = sanitizeSorts(component.sorts)
          if (sorts.length) {
            this.activeComponent.sorts = sorts
          } else {
            delete this.activeComponent.sorts
          }
        }
        this.activeComponent.filters = component.filters
        this.activeComponent.chartType = component.chartType
        this.activeComponent.labelField = component.labelField
        this.activeComponent.labelFieldLabel = component.labelFieldLabel
        this.activeComponent.valueField = component.valueField
        this.activeComponent.valueFields = component.valueFields
        this.activeComponent.valueFieldLabels = component.valueFieldLabels
        this.activeComponent.valueFieldTrendLines = component.valueFieldTrendLines
        this.activeComponent.aggregation = component.aggregation
        if (Array.isArray(component.rowFields)) {
          this.activeComponent.rowFields = component.rowFields
        }
        if (Array.isArray(component.columnFields)) {
          this.activeComponent.columnFields = component.columnFields
        }
        if (component.showRowGrandTotal !== undefined) {
          this.activeComponent.showRowGrandTotal = !!component.showRowGrandTotal
        }
        if (component.showColumnGrandTotal !== undefined) {
          this.activeComponent.showColumnGrandTotal = !!component.showColumnGrandTotal
        }
        if (component.showSubtotals !== undefined) {
          this.activeComponent.showSubtotals = !!component.showSubtotals
        }
        if (component.viewCode !== undefined) {
          this.activeComponent.viewCode = String(component.viewCode || '')
          this.activeComponent.viewCss = String(component.viewCss || '')
          this.activeComponent.exampleId = component.exampleId || ''
        }
        this.activeComponent.realtime = component.realtime
        if (Object.prototype.hasOwnProperty.call(component, 'cardsPerRow')) {
          const count = normalizeCardsPerRow(component.cardsPerRow)
          if (count) this.activeComponent.cardsPerRow = count
          else delete this.activeComponent.cardsPerRow
        }
        if (component.oneCardPerRow !== undefined) {
          this.activeComponent.oneCardPerRow = !!component.oneCardPerRow
        }
        applyPageRecordFilter(this.activeComponent, component.filterByPageRecord)
        if (component.addressColumn !== undefined) {
          this.activeComponent.addressColumn = component.addressColumn || ''
          this.activeComponent.latitudeColumn = component.latitudeColumn || ''
          this.activeComponent.longitudeColumn = component.longitudeColumn || ''
          this.activeComponent.mapStyleUrl = component.mapStyleUrl || ''
        }
        if (component.dateColumn !== undefined) {
          this.activeComponent.dateColumn = component.dateColumn || ''
          this.activeComponent.titleColumn = component.titleColumn || ''
          this.activeComponent.descriptionColumn = component.descriptionColumn || ''
          this.activeComponent.recurrenceColumn = component.recurrenceColumn || ''
          this.activeComponent.calendarLayout = component.calendarLayout || 'month'
          this.activeComponent.addFormUniqueTitle = component.addFormUniqueTitle || ''
        }
        if (component.csvExport !== undefined) {
          this.activeComponent.csvExport = !!component.csvExport
        }
        if (component.pageSize !== undefined) this.activeComponent.pageSize = component.pageSize || null
        if (component.pageSizeMax !== undefined) this.activeComponent.pageSizeMax = component.pageSizeMax || null
        if (component.wrapHeaders !== undefined) this.activeComponent.wrapHeaders = !!component.wrapHeaders
        if (component.hideEmptyColumns !== undefined) this.activeComponent.hideEmptyColumns = !!component.hideEmptyColumns
        if (component.twoFactor !== undefined) {

          if (component.twoFactor) this.activeComponent.twoFactor = true
          else delete this.activeComponent.twoFactor
        }
        this.activeComponent.submissionRules = component.submissionRules
        this.activeComponent.defaultValueRules = component.defaultValueRules
        this.activeComponent.formActions = component.formActions
        this.activeComponent.richTextContent = component.richTextContent
        this.activeComponent.staticImageId = component.staticImageId
        if (component.altText) this.activeComponent.altText = component.altText
        else delete this.activeComponent.altText
        if (Array.isArray(component.menuItems)) {
          this.activeComponent.menuItems = _.cloneDeep(component.menuItems)
        }

        return this.save()
      },
      pageIsModal (page) {

        return pageOpensAsModal(page)
      },
      openEmbed (mode, view) {

        this.embedStartMode = mode
        this.embedStartPage = (this.page && this.page.uniqueTitle) || ''
        this.embedStartView = (view && view.uniqueTitle) || ''
        this.embedChooserKey += 1
        this.embedModalVisible = true
      },
      showPageEmbed () {

        this.openEmbed('page')
      },
      showViewEmbed (view) {

        this.openEmbed('view', view)
      },
      showEditPage () {

        this.editPageTitle = this.page.title
        this.editPageUniqueTitle = this.page.uniqueTitle
        this.editPageShowBreadcrumbs = !!this.page.showBreadcrumbs
        this.editPageOpenAsModal = pageOpensAsModal(this.page)
        this.editPageHome = this.page.home === `true` || this.page.home === true
        this.copyPageWithSubPages = true
        this.copyingPage = false
        this.copyPageError = ``

        this.editPageModalVisible = true
      },
      showDeletePage () {

        this.deletePageUniqueTitle = this.page.uniqueTitle

        this.deletePageModalVisible = true
      },
      showEditMenu () {

        this.$router.push(`/${this.$route.params.application}/menu-editor`)
      },
      async createPage (event) {

        this.newPageSubmitAttempted = true

        if (!String(this.newPageTitle).trim().length || !String(this.newPageUniqueTitle).trim().length) {

          return event.preventDefault()
        }

        const body = {
          title: this.newPageTitle,
          uniqueTitle: this.newPageUniqueTitle
        }

        console.log(`create page!`)
        console.log(body)

        let newPage

        try {

          newPage = await this.$http.post(`${VUE_APP_PROXY_SERVER_URL}/pages/`, body, {headers: {'application-unique-title': this.$route.params.application}})
        } catch (error) {

          console.log(`error creating page:`)
          console.log(error)
          return event.preventDefault()
        }

        this.newPageTitle = ``
        this.newPageUniqueTitle = ``
        this.newPageUniqueTitleManuallyEdited = false
        this.newPageSubmitAttempted = false

        console.log(`newPage`)
        console.log(newPage)

        this.loadPage(newPage && newPage.data)
        this.getPages()
      },
      async createComponent (event) {

        this.newComponentSubmitAttempted = true

        const requiresSpreadsheet = this.componentTypeRequiresSpreadsheet

        if (!String(this.newComponentTitle).trim().length || !String(this.newComponentUniqueTitle).trim().length || (requiresSpreadsheet && !this.newComponentSpreadSheet) || !this.newComponentType) {

          return event.preventDefault()
        }

        let filterByPageRecord = false
        let resolvedSpreadSheet = this.newComponentSpreadSheet

        if (resolvedSpreadSheet.endsWith('::pageRecordFiltered')) {
          resolvedSpreadSheet = resolvedSpreadSheet.replace('::pageRecordFiltered', '')
          filterByPageRecord = true
        }

        let initialComponents = []

        if (this.addAllFieldsFromSpreadsheet && this.componentTypeSupportsAddAllFields) {

          event.preventDefault()

          try {

            const spreadsheet = await this.$http.get(`${VUE_APP_PROXY_SERVER_URL}/spreadsheets/${resolvedSpreadSheet}`, { headers: { 'application-unique-title': this.$route.params.application } })

            if (spreadsheet.data && spreadsheet.data.columns) {

              initialComponents = buildComponentsFromSpreadsheetColumns(spreadsheet.data.columns, this.newComponentType)
            }
          } catch (err) {

            console.error('Failed to fetch spreadsheet for add all fields:', err)
          }

          this.createComponentModalVisible = false
        }

        const newComponent = {
          title: this.newComponentTitle,
          uniqueTitle: this.newComponentUniqueTitle,
          type: this.newComponentType,
          spreadSheet: resolvedSpreadSheet,
          components: initialComponents
        }

        if (filterByPageRecord) {
          newComponent.filterByPageRecord = true
        }

        this.addComponentParentComponent.components.push(newComponent)

        const createdType = this.newComponentType
        const createdId = newComponent.uniqueTitle

        this.newComponentTitleManuallyEdited = false
        this.newComponentUniqueTitleManuallyEdited = false
        this.newComponentTitle = ``
        this.newComponentUniqueTitle = ``
        this.newComponentSpreadSheet = ``
        this.newComponentType = ``
        this.newComponentSubmitAttempted = false
        this.addAllFieldsFromSpreadsheet = false

        if (createdType === 'custom') {

          await this.save()
          const saved = this.findComponentByUniqueTitle(this.page, createdId)
          if (saved) this.showEditComponent(saved)
          return
        }

        this.save()
      },
      async editPage () {

        const body = _.cloneDeep(this.page)
        delete body.children

        body.title = this.editPageTitle
        body.uniqueTitle = this.editPageUniqueTitle
        body.uniqueTitlePriorToEdit = this.editingPageUniqueTitle
        body.showBreadcrumbs = this.editPageShowBreadcrumbs
        body.openAsModal = this.editPageOpenAsModal === true

        // The server persists with $set, so unsetting must write an explicit
        // empty value rather than omitting the key
        body.home = this.editPageHome === true ? `true` : ``

        console.log(`edit page!`)
        console.log(body)

        let newPage

        try {

          newPage = await this.$http.put(`${VUE_APP_PROXY_SERVER_URL}/pages/${this.page.uniqueTitle}`, body, {headers: {'application-unique-title': this.$route.params.application}})
        } catch (error) {

          console.log(`error editing page:`)
          console.log(error)
        }

        this.editPageTitle = ``
        this.editPageUniqueTitle = ``

        console.log(`newPage`)
        console.log(newPage)

        this.loadPage(newPage && newPage.data)

        this.getPages()
      },
      async copyPage () {

        if (this.copyingPage || !this.page || !this.page.uniqueTitle) return

        this.copyingPage = true
        this.copyPageError = ``

        try {

          const response = await this.$http.post(
            `${VUE_APP_PROXY_SERVER_URL}/pages/${this.page.uniqueTitle}/copy`,
            { includeSubPages: this.copyIncludesSubPages },
            { headers: { 'application-unique-title': this.$route.params.application } }
          )

          const created = response.data && response.data.page

          this.editPageModalVisible = false
          await this.getPages()

          const loaded = created && this.pages.find((item) => item.uniqueTitle === created.uniqueTitle)

          this.loadPage(loaded || created)
        } catch (error) {

          const message = error && error.response && error.response.data

          this.copyPageError = typeof message === 'string' && message ? message : `could not copy page`
        }

        this.copyingPage = false
      },
      showViewTransfer (view, mode) {

        if (!view || !this.page || !this.page.uniqueTitle) return

        const destinations = pagesForViewTransfer({
          pages: this.pages,
          spreadsheets: this.spreadsheets,
          sourcePageUniqueTitle: this.page.uniqueTitle,
          view,
          mode
        })

        this.viewTransferView = view
        this.viewTransferMode = mode === `move` ? `move` : `duplicate`
        this.viewTransferUniqueTitle = suggestedViewUniqueTitle(this.pages, view.uniqueTitle)
        this.viewTransferDestination = destinations[0] ? destinations[0].uniqueTitle : ``
        this.viewTransferError = ``
        this.viewTransferSubmitting = false
        this.viewTransferModalVisible = true
      },
      async confirmViewTransfer (event) {

        if (event && event.preventDefault) event.preventDefault()

        if (this.viewTransferSubmitting) return

        const plan = this.viewTransferPlan

        if (!plan.ok) {

          this.viewTransferError = plan.error
          return
        }

        const sourcePage = this.page.uniqueTitle
        const viewUniqueTitle = this.viewTransferView.uniqueTitle
        const destination = this.viewTransferDestination

        this.viewTransferSubmitting = true
        this.viewTransferError = ``

        try {

          await this.save()
          await this.$http.post(
            `${VUE_APP_PROXY_SERVER_URL}/pages/${sourcePage}/views/${viewUniqueTitle}/transfer`,
            {
              mode: this.viewTransferMode,
              destinationPageUniqueTitle: destination,
              uniqueTitle: this.viewTransferUniqueTitle
            },
            { headers: { 'application-unique-title': this.$route.params.application } }
          )
          this.viewTransferModalVisible = false
          await this.getPages()
          const loaded = this.pages.find((item) => item.uniqueTitle === destination)
          if (loaded) this.loadPage(loaded)
        } catch (error) {

          const message = error && error.response && error.response.data

          this.viewTransferError = typeof message === `string` && message ? message : `could not save the view`
        }

        this.viewTransferSubmitting = false
      },
      async deletePage () {

        try {

          await this.$http.delete(`${VUE_APP_PROXY_SERVER_URL}/pages/${this.page.uniqueTitle}`, {headers: {'application-unique-title': this.$route.params.application}})
        } catch (error) {

          console.log(`error deleting page:`)
          console.log(error)
        }

        this.editingPageUniqueTitle = ``

        this.getPages()
      },
      rememberPage (page) {

        if (!page || !page.uniqueTitle) return

        rememberDesignerSelection(window.sessionStorage, this.$route.params.application, {
          page: page.uniqueTitle
        })
      },
      loadPage (page) {

        console.log('design.vue loadPage called with:', page && page.uniqueTitle, page)

        if (!page) {

          page = {
            components: []
          }
        }

        this.page = page

        if (!this.page.components) {

          this.page.components = []
        }

        this.rememberPage(this.page)

        // Update URL to enable deep linking
        if (this.page.uniqueTitle) {
          const targetPath = `/${this.$route.params.application}/design/${this.page.uniqueTitle}`
          if (this.$route.path !== targetPath) {
            this.$router.replace(targetPath)
          }
        }

        this.$emit('page-changed', this.page)
        this.sendPageToPreview()
      },

      // -- Preview iframe communication --
      buildPreviewUrl () {
        const app = this.$route.params.application
        if (!app) return null
        return liveAppHref(window.location, window.__SUPERTURBO_API__, app, '__preview__')
      },

      clearPreviewWait () {

        if (this.previewWaitTimer) {

          clearTimeout(this.previewWaitTimer)
          this.previewWaitTimer = null
        }
      },

      armPreviewWait () {

        this.clearPreviewWait()
        this.previewStalled = false
        this.previewWaitTimer = setTimeout(() => {

          if (!this.previewReady) this.previewStalled = true
        }, 8000)
      },

      onPreviewIframeLoad () {

        const iframe = this.$refs.previewIframe

        if (iframe && iframe.contentWindow) {

          iframe.contentWindow.postMessage({ type: 'superturbo-preview-ping' }, '*')
        }

        setTimeout(() => {
          this.sendPageToPreview()
        }, 500)
      },

      sendPageToPreview () {
        const iframe = this.$refs.previewIframe
        if (!iframe || !iframe.contentWindow) return
        if (!this.page || !this.page.uniqueTitle) return

        iframe.contentWindow.postMessage({
          type: 'superturbo-preview-page',
          page: JSON.parse(JSON.stringify(this.page)),
          application: this.$route.params.application
        }, '*')
      },

      onPreviewMessage (event) {
        if (event.data && event.data.type === 'superturbo-preview-ready') {
          this.previewReady = true
          this.previewStalled = false
          this.clearPreviewWait()
          this.sendPageToPreview()
          if (this.appCustomCss) {
            this.sendAppCssToPreview(this.appCustomCss)
          }
        }
      },

      // -- Split pane drag logic --
      onDividerMouseDown (event) {

        event.preventDefault()
        this.isDraggingDivider = true

        const iframe = this.$refs.previewIframe
        if (iframe) iframe.style.pointerEvents = 'none'

        const stacked = this.compactDesign
        const main = stacked ? this.$el.querySelector('.design-main') : null

        const onMouseMove = (e) => {

          if (stacked && main) {

            const bounds = main.getBoundingClientRect()
            const next = e.clientY - bounds.top
            const min = 180
            const max = Math.max(min, bounds.height - 160)

            this.stackedEditorHeight = Math.max(min, Math.min(max, next))
            return
          }

          this.editorWidth = Math.max(500, Math.min(e.clientX, window.innerWidth - 300))
        }
        const onMouseUp = () => {
          this.isDraggingDivider = false
          if (iframe) iframe.style.pointerEvents = ''
          document.removeEventListener('mousemove', onMouseMove)
          document.removeEventListener('mouseup', onMouseUp)
        }
        document.addEventListener('mousemove', onMouseMove)
        document.addEventListener('mouseup', onMouseUp)
      },
      syncCompactDesign () {

        const compact = window.innerWidth < 900

        if (compact === this.compactDesign) return

        this.compactDesign = compact
        this.stackedEditorHeight = 0
      },
    },
    directives: {
      hoverTip
    },
    components: {
      draggable,
      pageTree,
      DragHandle,
      formEdit,
      tableEdit,
      pivotEdit,
      customEdit,
      cardsEdit,
      chartEdit,
      richtextEdit,
      staticImageEdit,
      menuEdit,
      ViewCssEditor,
      AiTokenUsage,
      EmbedChooser,
      UserPermissionRuleEditor,
      PageOpenRuleEditor,
      ComponentTypePicker
    }
  }
</script>

<style>
.design-root {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.design-context-nav {
  flex-shrink: 0;
}

.design-main {
  flex: 1;
  display: flex;
  min-height: 0;
  overflow: hidden;
}

.design-editor-pane {
  overflow-y: auto;
  overflow-x: hidden;
  flex-shrink: 0;
  min-width: 500px;
  position: relative;
}

.design-divider {
  width: 6px;
  cursor: col-resize;
  background-color: #1e1e1b;
  border-left: 1px solid #2e2e2b;
  border-right: 1px solid #2e2e2b;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background-color 0.15s;
}

.design-divider:hover,
.design-divider-dragging {
  background-color: #2a2a27;
  border-color: #9ad12b;
}

.design-divider-handle {
  width: 2px;
  height: 40px;
  background-color: #3a3a37;
  border-radius: 1px;
}

.design-divider:hover .design-divider-handle,
.design-divider-dragging .design-divider-handle {
  background-color: #9ad12b;
}

.design-preview-pane {
  position: relative;
  flex: 1;
  min-width: 300px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: #fff;
}

.design-preview-pane.is-waiting {
  background-color: #181916;
}

.design-preview-pane.is-waiting .preview-iframe {
  visibility: hidden;
}

.preview-waiting {
  position: absolute;
  top: 28px;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 24px;
  text-align: center;
  color: #c8c8c4;
  background-color: #181916;
}

.preview-waiting p {
  margin: 0;
}

.preview-waiting-note {
  max-width: 280px;
  color: #a8a8a4;
  font-size: 0.85rem;
}

.preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 12px;
  background-color: #141412;
  border-bottom: 1px solid #2e2e2b;
  flex-shrink: 0;
  height: 28px;
}

.preview-header-label {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #a8a8a4;
}

.preview-header-status {
  font-size: 0.68rem;
  color: #a8a8a4;
}

.preview-header-status.connected {
  color: #9ad12b;
}

.preview-header-status.stalled {
  color: #e0b15a;
}

.preview-iframe {
  flex: 1;
  width: 100%;
  border: none;
}

.preview-toggle-btn {
  font-size: 0.75rem !important;
  padding: 2px 10px !important;
  opacity: 0.6;
}

.preview-toggle-btn:hover {
  opacity: 1;
}

.preview-toggle-active {
  opacity: 1 !important;
  border-color: #9ad12b !important;
  color: #9ad12b !important;
}

.preview-placeholder {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a8a8a4;
  font-size: 0.85rem;
  background-color: #181916;
}

.delete {
  background: transparent;
  font-size: 12px !important;
  font-weight: bolder;
  color: #f82471 !important;
  text-shadow: none !important;
  border: 1px #f82471 solid;
  flex: 0 0 25px !important;
  max-width: 25px !important;
  min-width: 25px !important;
  padding: 0 !important;
  text-align: center !important;
  cursor: pointer;
  border-radius: 3px;
  transition: background 0.15s, color 0.15s;
}
.delete:hover {
  background-color: #f82471;
  color: #fff !important;
}

#context-nav {
  background-color: #181915 !important;
  border-bottom: 1px #2e2e2b solid;
}

#page-list-col {
  padding: 0 !important;
  flex: 0 0 220px;
  width: 220px;
  max-width: 220px;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

.design-editor-pane .row {
  flex-wrap: nowrap;
  height: 100%;
  min-height: 0;
}

.design-editor-pane .design-canvas-col {
  flex: 1 1 0;
  width: auto;
  max-width: none;
  min-width: 0;
}

.design-main-stacked {
  flex-direction: column;
}

.design-main-stacked .design-editor-pane {
  min-width: 0;
  min-height: 0;
}

.design-main-stacked .design-preview-pane {
  flex: 1 1 0;
  min-width: 0;
  min-height: 160px;
}

.design-main-stacked .design-divider {
  width: 100%;
  height: 8px;
  cursor: row-resize;
  border-left: 0;
  border-right: 0;
  border-top: 1px solid #2e2e2b;
  border-bottom: 1px solid #2e2e2b;
}

.design-main-stacked .design-divider-handle {
  width: 40px;
  height: 2px;
}

.sidebar {
  background-color: #141412;
  border-right: 1px solid #2e2e2b;
  height: 100%;
  overflow-y: auto;
  padding-bottom: 32px;
}

.sidebar-section-label {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #a8a8a4;
  padding: 14px 14px 8px;
}

.page-root-drop {
  display: flex;
  align-items: center;
  height: 22px;
  margin: 4px 8px;
  padding: 0 8px;
  border: 1px dashed #7aa33a;
  border-radius: 3px;
  background: rgba(154, 209, 43, 0.12);
  color: #9ad12b;
  font-size: 0.68rem;
  line-height: 18px;
}

.context-nav-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  width: 100%;
}
.context-nav-end {
  margin-left: auto;
}
button.sidebar-open,
button.view-card-open {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  padding: 0;
  cursor: pointer;
}
button.view-card-open {
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  padding: 8px 10px;
}
.sidebar-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px;
  cursor: pointer;
  color: #b0b0ae;
  font-size: 0.875rem;
  transition: background 0.1s, color 0.1s;
  user-select: none;
}
.sidebar-item:hover {
  background-color: #1e1e1b;
  color: #e0e0de;
}
.sidebar-item.active {
  background-color: #1a1a17;
  color: #e8e8e6;
  border-left: 2px solid #9ad12b;
  padding-left: 12px;
}

.sidebar-icon {
  font-size: 0.75rem;
  color: #4a4a47;
  flex-shrink: 0;
}
.sidebar-item.active .sidebar-icon {
  color: #9ad12b;
}

.sidebar-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidebar-group {
  position: relative;
}

.sidebar-children {
  background-color: #111110;
}

.page-nest-hint,
.sidebar.page-dragging .page-nest-drop.page-nest-allowed.is-empty::after,
.sidebar.page-dragging .page-nest-drop.page-nest-allowed:not(.is-empty)::before {
  color: #9ad12b;
  font-size: 0.68rem;
  line-height: 18px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar.page-dragging .page-nest-drop.page-nest-allowed.is-empty {
  pointer-events: auto;
  position: static;
  display: block;
  height: auto;
  min-height: 22px;
  margin: 2px 8px 4px 0;
  padding: 2px 0;
  overflow: visible;
  border: 1px dashed #7aa33a;
  border-radius: 3px;
  background: rgba(154, 209, 43, 0.12);
}

.sidebar.page-dragging .page-nest-drop.page-nest-allowed.is-empty::after {
  content: attr(data-nest-label);
  display: block;
  position: static;
  height: 18px;
  padding: 0 8px;
  pointer-events: none;
}

.sidebar.page-dragging .page-nest-drop.page-nest-allowed.is-empty:hover {
  border-color: #c6ef62;
  background: rgba(154, 209, 43, 0.22);
}

.sidebar.page-dragging .page-nest-drop.page-nest-allowed:not(.is-empty) {
  padding-top: 0;
  background-image: none;
  box-shadow: none;
}

.sidebar.page-dragging .page-nest-drop.page-nest-allowed:not(.is-empty)::before {
  content: attr(data-nest-label);
  display: flex;
  align-items: center;
  box-sizing: border-box;
  height: 22px;
  margin: 2px 8px 4px 0;
  padding: 0 8px;
  position: static;
  top: auto;
  border: 1px dashed #7aa33a;
  border-radius: 3px;
  background: rgba(154, 209, 43, 0.12);
  pointer-events: none;
}

.content-empty {
  margin: 24px 8px;
  color: #9ad12b;
}
.content-empty p {
  margin-bottom: 12px;
}
.design-canvas-col {
  padding: 0 !important;
  overflow: auto;
  min-width: 0;
}

.layout-canvas-wrap {
  padding: 12px 16px 28px;
}

.layout-canvas {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}

.layout-row,
.layout-column,
.view-card {
  min-width: 0;
  border: 1px solid #3a3a37;
  border-radius: 6px;
}

.layout-row {
  background: #141412;
  width: max-content;
  min-width: 100%;
  box-sizing: border-box;
}

.layout-chrome {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 32px;
  padding: 4px 8px 4px 10px;
  border-bottom: 1px solid #2e2e2b;
}

.layout-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #9ad12b;
  font-size: 0.75rem;
  line-height: 1.2;
}

.layout-columns {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 10px 0;
}

.layout-columns-sized {
  gap: 0;
  flex-wrap: nowrap;
  width: max-content;
  min-width: 100%;
}

.layout-columns-sized .layout-column {
  min-width: 240px;
}

.layout-chrome-actions {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.layout-format-btn {
  width: 20px;
  height: 20px;
  min-width: 20px;
  flex: 0 0 20px;
  font-size: 10px;
}

.layout-format-btn.active {
  color: #9ad12b;
  border-color: #9ad12b;
  background: rgba(154, 209, 43, 0.12);
}

.layout-width-label {
  margin-left: 6px;
  color: #c8e88a;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

.layout-column {
  flex: 1 1 280px;
  min-width: min(100%, 240px);
  display: flex;
  flex-direction: column;
  background: #10100e;
  border-radius: 5px;
}

.layout-views {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  padding: 8px 8px 0;
  min-height: 8px;
}

.view-card {
  width: 100%;
  background: #1a1a17;
  border-radius: 4px;
  cursor: pointer;
  overflow: hidden;
  transition: border-color 0.15s;
}

.view-card:hover {
  border-color: #5c5c59;
}

.view-card-bar,
.view-card-type,
.view-card-actions {
  display: flex;
  align-items: center;
}

.view-card-bar {
  justify-content: space-between;
  gap: 8px;
  min-height: 30px;
  padding: 4px 6px 4px 8px;
  border-bottom: 1px solid #2e2e2b;
  color: #9ad12b;
  font-size: 0.75rem;
}

.view-card-type {
  min-width: 0;
}

.view-card-actions {
  gap: 6px;
  flex-shrink: 0;
}
.view-card-actions > .css-edit-btn,
.view-card-actions > .delete {
  position: static;
  flex: 0 0 auto;
}

.view-card-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 10px;
}

.view-card-title {
  font-weight: 600;
  font-size: 0.95rem;
  line-height: 1.25;
  color: #e8e8e6;
}

.view-card-title.is-placeholder {
  color: #a8a8a4;
  font-weight: 400;
  font-style: italic;
}

.view-card-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.view-card-slug {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.75rem;
  color: #8a8a88;
  line-height: 1.3;
}

.view-card-meta .view-default-rule-badge {
  margin-left: 0;
}

.view-card-body .view-login-controls {
  padding-top: 4px;
}

.view-card-foot {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  padding: 6px 10px;
  border-top: 1px solid #2e2e2b;
  font-size: 0.75rem;
  line-height: 1.35;
  color: #f82471;
}

.login-sheet-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  flex-shrink: 0;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #9ad12b;
  background: rgba(154, 209, 43, 0.12);
  border: 1px solid rgba(154, 209, 43, 0.45);
  border-radius: 999px;
  padding: 0 6px;
  line-height: 1.5;
}

.view-card-foot-via {
  color: #6ab0de;
}

.layout-canvas .delete {
  width: 20px !important;
  height: 20px;
  flex: 0 0 20px !important;
  max-width: 20px !important;
  min-width: 20px !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.layout-add {
  display: block;
  width: calc(100% - 16px);
  margin: 8px;
  padding: 7px 10px;
  text-align: left;
  color: #9ad12b;
  background: transparent;
  border: 1px dashed #3f4a32;
  border-radius: 4px;
  font: inherit;
  font-size: 0.8rem;
  line-height: 1.2;
  cursor: pointer;
}

.layout-add-column {
  width: calc(100% - 20px);
  margin: 10px;
}

.layout-add-row {
  width: 100%;
  margin: 0;
}

.layout-add:hover {
  color: #b8f046;
  background: #1e1e1b;
  border-color: #9ad12b;
  border-style: solid;
}

.content-header {
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 12px 16px 10px;
  border-bottom: 1px solid #2e2e2b;
}
.content-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #e8e8e6;
}
.content-unique-title {
  font-size: 0.8rem;
  color: #9ad12b;
  font-family: monospace;
}
.content-modal-caption {
  font-size: 0.75rem;
  color: #6ab0de;
}
.content-header-lock {
  cursor: pointer;
  border: 0;
  background: transparent;
  color: #b0b0ae;
  font-size: 0.85rem;
  padding: 2px 6px;
  border-radius: 3px;
  transition: color 0.15s, background 0.15s;
}
.content-header-lock:hover {
  color: #c0c0be;
  background: #2a2a27;
}
.content-header-lock.active {
  color: #9ad12b;
}

.drag-handle {
  cursor: grab;
  color: #7a7a76;
  font-size: 0.65rem;
  flex-shrink: 0;
  padding: 2px 2px 2px 0;
  transition: color 0.15s;
}
.drag-handle:hover {
  color: #9ad12b;
}
.drag-handle:active {
  cursor: grabbing;
}

.drag-ghost {
  opacity: 0.4;
  background-color: #1a1a17;
  border-left: 2px solid #9ad12b;
}

.page-drag-fallback {
  pointer-events: none;
  z-index: 100000;
  opacity: 0.92;
  background: #1a1a17;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.45);
}

.view-drag-handle {
  cursor: grab;
  color: #7a7a76;
  font-size: 0.65rem;
  margin-right: 4px;
  padding: 2px;
  transition: color 0.15s;
}
.view-drag-handle:hover {
  color: #9ad12b;
}
.view-drag-handle:active {
  cursor: grabbing;
}

.view-drag-ghost {
  opacity: 0.5;
  background-color: #1a1a17;
  border: 2px dashed #9ad12b;
}

.create-component-modal-body .form-check-label,
.create-component-modal-body label {
  color: #b0b0ae;
}

.component-type-hint {
  margin: 0 0 8px;
  font-size: 0.8rem;
  color: #8a8a88;
}

.css-edit-btn {
  box-sizing: border-box;
  font-size: 11px;
  color: #b0b0ae;
  border: 1px solid #474842;
  border-radius: 3px;
  padding: 0;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.view-card-actions > .css-edit-btn {
  width: 22px;
  min-width: 22px;
  height: 20px;
  flex: 0 0 22px;
}
.css-edit-btn:hover {
  background-color: #9ad12b;
  border-color: #9ad12b;
  color: #1a1a17;
}

.page-css-btn {
  margin-left: auto;
  font-size: 0.8rem;
  padding: 2px 8px;
  height: auto;
  width: auto;
  min-width: auto;
  flex: 0 0 auto;
}

.app-css-btn {
  font-size: 0.75rem !important;
  padding: 2px 10px !important;
  color: #b0b0ae !important;
  border-color: #474842 !important;
  background: transparent !important;
}
.app-css-btn:hover {
  background-color: #9ad12b !important;
  border-color: #9ad12b !important;
  color: #1a1a17 !important;
}

.view-login-controls {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 2px 10px 10px;
}

.view-login-btn {
  box-sizing: border-box;
  width: 22px;
  height: 22px;
  flex: 0 0 22px;
  margin: 0;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 3px;
  font-size: 11px;
  line-height: 1;
  color: #a8a8a4;
  border: 1px #3a3a37 solid;
  background: transparent;
}

.view-login-btn.active {
  color: #9ad12b;
  border-color: #9ad12b;
  background-color: rgba(154, 209, 43, 0.1);
}

.view-hide-logged-in-btn.active {
  color: #f0ad4e;
  border-color: #f0ad4e;
  background-color: rgba(240, 173, 78, 0.1);
}

.view-login-caption {
  font-size: 0.7rem;
  color: #9ad12b;
  line-height: 1.2;
}

.view-login-caption-logged-in {
  color: #f0ad4e;
}

.view-default-rule-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-left: 4px;
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: #9ad12b;
  background: rgba(154, 209, 43, 0.12);
  border: 1px solid rgba(154, 209, 43, 0.35);
  border-radius: 3px;
  padding: 0 5px;
  line-height: 1.5;
  vertical-align: middle;
}

.copy-subpages-label {
  margin: 4px 0 2px;
}

.copy-page-slug {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  color: #8a8a88;
}

.copy-page-preview {
  margin: 8px 0 0;
  color: #b0b0ae;
  font-size: 0.85em;
}

.copy-page-btn {
  display: inline-block;
  margin-top: 10px;
  background: transparent;
  color: #e8e8e6;
  border: 1px solid #4a4a46;
  border-radius: 4px;
  padding: 6px 14px;
  font-size: 0.85em;
  cursor: pointer;
}

.copy-page-btn:hover:not(:disabled) {
  border-color: #9ad12b;
  color: #9ad12b;
}

.copy-page-btn:disabled {
  opacity: 0.55;
  cursor: default;
}

.copy-page-error {
  color: #e07070;
  font-size: 0.8em;
  margin: 8px 0 0;
}

.hover-tip-bubble {
  position: fixed;
  z-index: 2000;
  max-width: 240px;
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid #5a5a57;
  background: #2a2a27;
  color: #e8e8e6;
  font-family: "Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.35;
  letter-spacing: 0;
  text-transform: none;
  white-space: normal;
  pointer-events: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.45);
}
</style>
